import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import Stepper, { Step } from './Stepper';
import { GAS_DISTRIBUTOR_INITIAL_STATE, GAS_DISTRIBUTOR_LISTS } from '../config/gasDistributorConfig';
import './CreateProjectModal.css';
import AppIcon from './icons/AppIcon';

export default function CreateProjectModal({ 
  isOpen, 
  onClose, 
  onCreate, 
  users = [],
  initialData = null
}) {
  const getProjectType = (pd) => String(pd?.project_type || '').trim();

  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    members: [],
    allUsersSelected: true,
    project_type: 'custom',
    icon: 'dashboard',
    color: '#6366f1',
    backgroundImage: null,
    backgroundImagePreview: null,
    connections: [],
    agents: [],
    detailedDescription: '',
    barRestaurantProjectType: 'service_management',
    barRestaurantTeam: [{ id: 1, name: '', phone: '' }],
    barRestaurantFiles: [],
    barRestaurantExtractedText: '', // Texto extraído do arquivo
    barRestaurantHouseInfo: '',
    barRestaurantAgentDraft: {
      name: 'Atendente do Salão',
      prompt: '',
    },
    barRestaurantAdminAgentDraft: {
      name: 'Admin - Gerenciador do Grupo',
      prompt: '',
    },
    // Configurações CRM
    useForm: false,
    formFields: [
      { id: 1, name: 'Nome Completo', icon: 'person', required: true },
      { id: 2, name: 'Email', icon: 'email', required: true },
      { id: 3, name: 'Telefone', icon: 'phone', required: true },
      { id: 4, name: 'Empresa', icon: 'business', required: false },
      { id: 5, name: 'Interesse/Produto', icon: 'interests', required: false }
    ],
    // Configurações Gas Distributor
    ...GAS_DISTRIBUTOR_INITIAL_STATE
  });

  // Estados para edição de campos
  const [editingField, setEditingField] = useState(null);
  const [editFieldName, setEditFieldName] = useState('');
  const [editFieldRequired, setEditFieldRequired] = useState(false);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldIcon, setNewFieldIcon] = useState('label');
  const [newFieldRequired, setNewFieldRequired] = useState(false);

  // Preencher dados quando estiver editando
  useEffect(() => {
    if (initialData) {
      setProjectData({
        name: initialData.name || '',
        description: initialData.description || '',
        members: initialData.members || [],
        allUsersSelected: initialData.all_users_selected || false,
        project_type: initialData.project_type || 'custom',
        icon: initialData.icon || 'dashboard',
        color: initialData.color || '#6366f1',
        backgroundImage: null,
        backgroundImagePreview: initialData.background_image || null,
        backgroundImageUrl: initialData.background_image || null,
        connections: initialData.connections || [],
        agents: initialData.agents || [],
        detailedDescription: initialData.detailed_description || ''
      });
    }
  }, [initialData]);
  

  // Carregar dados da empresa quando o modal abrir ou tipo mudar para gas_distributor
  useEffect(() => {
    if (isOpen && getProjectType(projectData) === 'gas_distributor') {
      loadCompanyDataForEtapa6();
    }
  }, [isOpen, projectData.project_type]);

  const loadCompanyDataForEtapa6 = async () => {
    try {
      setLoadingCompanyData(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      
      if (!user.company_id) {
        console.warn('[CreateProjectModal] company_id não encontrado');
        return;
      }

      const { data: company, error } = await supabase
        .from('configuracoes_empresa')
        .select('*')
        .eq('company_id', user.company_id)
        .single();

      if (error) {
        console.warn('[CreateProjectModal] Erro ao buscar dados da empresa:', error);
        return;
      }

      if (company) {
        // Preencher dados gerais da empresa
        setCompanyDataEtapa6({
          nomeFantasia: company.nome_fantasia || '',
          descricao: company.descricao || '',
          logradouro: company.logradouro || '',
          numero: company.numero || '',
          complemento: company.complemento || '',
          bairro: company.bairro || '',
          cidade: company.cidade || '',
          estado: company.estado || '',
          cep: company.cep || '',
          telefonePrincipal: company.telefone_principal || '',
          telefoneSecundario: company.telefone_secundario || '',
          emailContato: company.email_contato || '',
          site: company.site || '',
          redesSociais: company.redes_sociais || {
            instagram: '',
            facebook: '',
            linkedin: '',
            twitter: ''
          }
        });

        // Preencher endereço base estruturado com dados da empresa
        setEnderecoBaseEstruturado({
          cep: company.cep || '',
          logradouro: company.logradouro || '',
          numero: company.numero || '',
          complemento: company.complemento || '',
          bairro: company.bairro || '',
          cidade: company.cidade || '',
          estado: company.estado || ''
        });

        console.log('[CreateProjectModal] ✅ Dados da empresa carregados para Etapa 6');
      }
    } catch (err) {
      console.error('[CreateProjectModal] Erro ao carregar dados da empresa:', err);
    } finally {
      setLoadingCompanyData(false);
    }
  };

  const [activeIntegrationTab, setActiveIntegrationTab] = useState('connections');
  const [connections, setConnections] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const prevStepRef = useRef(1);

  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [promptError, setPromptError] = useState(null);

  // Estados para Gas Distributor - Etapa 4
  const [selectedConnectionDetails, setSelectedConnectionDetails] = useState(null);
  const [loadingConnectionDetails, setLoadingConnectionDetails] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [editingConnectionName, setEditingConnectionName] = useState(false);
  const [tempConnectionName, setTempConnectionName] = useState('');

  // Estados para Gas Distributor - Etapa 5 (Produtos)
  const [productCategories, setProductCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Estados para Gas Distributor - Etapa 6 (Configuração Completa)
  const [horariosFuncionamento, setHorariosFuncionamento] = useState({
    segunda: { abertura: '08:00', fechamento: '18:00', ativo: true },
    terca: { abertura: '08:00', fechamento: '18:00', ativo: true },
    quarta: { abertura: '08:00', fechamento: '18:00', ativo: true },
    quinta: { abertura: '08:00', fechamento: '18:00', ativo: true },
    sexta: { abertura: '08:00', fechamento: '18:00', ativo: true },
    sabado: { abertura: '08:00', fechamento: '14:00', ativo: true },
    domingo: { abertura: '08:00', fechamento: '14:00', ativo: false }
  });
  const [areasCobertura, setAreasCobertura] = useState([]);
  const [novaArea, setNovaArea] = useState({ nome: '', taxa: '', tempo: '' });
  
  // Endereço Base - Estruturado com dados da empresa
  const [enderecoBaseEstruturado, setEnderecoBaseEstruturado] = useState({
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: ''
  });
  
  // Formas de Pagamento - Estruturado com configurações específicas
  const [formasPagamentoEstruturado, setFormasPagamentoEstruturado] = useState([
    {
      tipo: 'dinheiro',
      ativo: true,
      naEntrega: true,
      observacoes: ''
    },
    {
      tipo: 'pix',
      ativo: true,
      tipoPix: 'chave', // 'chave', 'telefone', 'email', 'cpf'
      chavePix: '',
      observacoes: ''
    },
    {
      tipo: 'cartao',
      ativo: false,
      naEntrega: false,
      maquinaPagamento: '', // Nome da máquina/operadora
      observacoes: ''
    }
  ]);
  
  const [novaFormaPagamento, setNovaFormaPagamento] = useState({
    tipo: '',
    nome: '',
    observacoes: ''
  });
  
  const [tempoMedioEntrega, setTempoMedioEntrega] = useState('60');
  const [politicaEntrega, setPoliticaEntrega] = useState('');
  const [politicaCancelamento, setPoliticaCancelamento] = useState('');
  const [politicaVendas, setPoliticaVendas] = useState('');

  // Estados para Etapa 6 - Dados da Empresa (pré-preenchidos)
  const [companyDataEtapa6, setCompanyDataEtapa6] = useState({
    nomeFantasia: '',
    descricao: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    telefonePrincipal: '',
    telefoneSecundario: '',
    emailContato: '',
    site: '',
    redesSociais: {
      instagram: '',
      facebook: '',
      linkedin: '',
      twitter: ''
    }
  });
  const [loadingCompanyData, setLoadingCompanyData] = useState(false);

  const generateBarRestaurantPrompt = async (pd) => {
    try {
      setGeneratingPrompt(true);
      setPromptError(null);

      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      
      // Buscar dados da empresa no Supabase
      let companyInfo = '';
      try {
        const { data: company, error } = await supabase
          .from('configuracoes_empresa')
          .select('*')
          .eq('company_id', user.company_id)
          .single();

        if (!error && company) {
          companyInfo = `
## INFORMAÇÕES DA EMPRESA
Nome: ${company.nome || 'Não informado'}
Segmento: ${company.segmento || 'Não informado'}
Subsegmento: ${company.subsegmento || 'Não informado'}
Descrição: ${company.descricao || 'Não informado'}
`;
          console.log('[CreateProjectModal] ✅ Dados da empresa carregados');
        }
      } catch (err) {
        console.warn('[CreateProjectModal] ⚠️ Erro ao buscar dados da empresa:', err.message);
      }

      // Preparar dados para enviar ao backend
      const promptData = {
        name: pd?.name || 'Bar & Restaurante',
        description: pd?.description || '',
        barRestaurantTeam: pd?.barRestaurantTeam || [],
        barRestaurantExtractedText: pd?.barRestaurantExtractedText || '', // Texto extraído do documento
        barRestaurantHouseInfo: pd?.barRestaurantHouseInfo || '',
        companyInfo: companyInfo // Dados da empresa
      };

      console.log('[CreateProjectModal] Enviando dados para gerar prompt...');

      // Chamar backend para gerar prompt
      const response = await fetch(`${BACKEND_NODE_URL}/api/bar-restaurant/generate-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token || ''}`
        },
        body: JSON.stringify(promptData)
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Erro ao gerar prompt');
      }

      console.log('[CreateProjectModal] ✅ Prompt gerado com sucesso');
      return result.prompt;

    } catch (error) {
      console.error('[CreateProjectModal] ❌ Erro ao gerar prompt:', error.message);
      setPromptError(error.message);
      
      // Retornar prompt padrão em caso de erro
      return getDefaultBarRestaurantPrompt(pd);
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const generateBarRestaurantAdminPrompt = async (pd) => {
    try {
      setGeneratingPrompt(true);
      setPromptError(null);

      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      
      // Buscar dados da empresa no Supabase
      let companyInfo = '';
      try {
        const { data: company, error } = await supabase
          .from('configuracoes_empresa')
          .select('*')
          .eq('company_id', user.company_id)
          .single();

        if (!error && company) {
          companyInfo = `
## INFORMAÇÕES DA EMPRESA
Nome: ${company.nome || 'Não informado'}
Segmento: ${company.segmento || 'Não informado'}
Subsegmento: ${company.subsegmento || 'Não informado'}
Descrição: ${company.descricao || 'Não informado'}
`;
          console.log('[CreateProjectModal] ✅ Dados da empresa carregados para Admin');
        }
      } catch (err) {
        console.warn('[CreateProjectModal] ⚠️ Erro ao buscar dados da empresa:', err.message);
      }

      // Preparar dados para enviar ao backend
      const promptData = {
        name: pd?.name || 'Bar & Restaurante',
        description: pd?.description || '',
        barRestaurantTeam: pd?.barRestaurantTeam || [],
        barRestaurantExtractedText: pd?.barRestaurantExtractedText || '',
        barRestaurantHouseInfo: pd?.barRestaurantHouseInfo || '',
        companyInfo: companyInfo
      };

      console.log('[CreateProjectModal] Enviando dados para gerar prompt Admin...');

      // Chamar backend para gerar prompt do Admin
      const response = await fetch(`${BACKEND_NODE_URL}/api/bar-restaurant/generate-admin-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token || ''}`
        },
        body: JSON.stringify(promptData)
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Erro ao gerar prompt Admin');
      }

      console.log('[CreateProjectModal] ✅ Prompt Admin gerado com sucesso');
      return result.prompt;

    } catch (error) {
      console.error('[CreateProjectModal] ❌ Erro ao gerar prompt Admin:', error.message);
      setPromptError(error.message);
      
      // Retornar prompt padrão em caso de erro
      return getDefaultBarRestaurantAdminPrompt(pd);
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const getDefaultBarRestaurantAdminPrompt = (pd) => {
    const team = (pd?.barRestaurantTeam || [])
      .filter(m => m.name || m.phone)
      .map(m => `- ${m.name || 'Integrante'} (${m.phone || 'sem número'})`)
      .join('\n');

    const houseInfo = String(pd?.barRestaurantHouseInfo || '').trim();

    return `# 👨‍💼 AGENTE ADMIN - GERENCIADOR DO GRUPO - ${pd?.name || 'Restaurante'}

## IDENTIDADE
Você é o Agente Admin, responsável por gerenciar o grupo WhatsApp do restaurante. Sua função é:
- Receber pedidos já estruturados do Atendente do Salão
- Verificar e gerenciar mesas
- Coordenar as listas de kanban do projeto
- Manter a comunicação fluida no grupo

## OBJETIVO PRINCIPAL
Garantir que todos os pedidos sejam processados corretamente, as mesas sejam gerenciadas de forma eficiente, e o kanban do projeto esteja sempre atualizado.

## COMO FUNCIONA O FLUXO

### 1. RECEBIMENTO DO PEDIDO
O Atendente do Salão envia um pedido estruturado como:
\`\`\`
📋 **PEDIDO CONFIRMADO**
Mesa: 4
Itens:
- Coca Cola (qtd: 1)
- Batata Frita (qtd: 2)
Observações: nenhuma
\`\`\`

### 2. VERIFICAÇÃO DE MESA
Ao receber o pedido, você deve:
1. Verificar se a mesa 4 já existe no sistema
2. Se SIM: adicionar o novo pedido à mesa existente
3. Se NÃO: criar a mesa e adicionar o pedido

### 3. PROCESSAMENTO NO KANBAN
Após processar:
1. Atualizar o status do pedido no kanban
2. Mover para a coluna apropriada (Novo, Em Preparo, Pronto, Entregue)
3. Registrar no banco de dados

### 4. RESPOSTA NO GRUPO
Envie uma confirmação no grupo com o formato:

\`\`\`
✅ **PEDIDO PROCESSADO**
Mesa: 4
Status: Novo
Itens: 3
Total: R$ XX,XX

[Preparando] [Pronto] [Entregue] [Cancelar]
\`\`\`

## CONTEXTO DO RESTAURANTE

**Projeto:** ${pd?.name || 'Restaurante'}

**Integrantes (WhatsApp):**
${team || '(não informado)'}

**Informações da Casa:**
${houseInfo || '(não informado)'}

## REGRAS IMPORTANTES
- Sempre verifique se a mesa existe antes de criar uma nova
- Responda rapidamente (máximo 30 segundos)
- Mantenha o kanban sempre atualizado
- Não invente informações sobre pedidos
- Use os botões para facilitar ações no grupo
- Registre todas as alterações no banco de dados

---
**Nota:** Esta conversa acontece no grupo WhatsApp do restaurante e será salva como histórico.`;
  };

  const getDefaultBarRestaurantPrompt = (pd) => {
    const team = (pd?.barRestaurantTeam || [])
      .filter(m => m.name || m.phone)
      .map(m => `- ${m.name || 'Integrante'} (${m.phone || 'sem número'})`)
      .join('\n');

    const houseInfo = String(pd?.barRestaurantHouseInfo || '').trim();

    return `# 🍽️ ATENDENTE DO SALÃO - ${pd?.name || 'Restaurante'}

## IDENTIDADE
Você é o Atendente do Salão, um agente interno responsável por receber, validar e estruturar pedidos dos garçons via WhatsApp.

## OBJETIVO PRINCIPAL
Garantir que todos os pedidos sejam registrados de forma clara, estruturada e sem erros, facilitando o trabalho da cozinha e do bar.

## COMO RECEBER PEDIDOS
Os garçons enviarão mensagens como:
- "Mesa 4: 1 coca, 1 batata frita, 2 cervejas"
- "Mesa 7: 1 água com gás, 1 suco de laranja"
- "Balcão: 3 cafés, 2 pães de queijo"

## PROCESSAMENTO DO PEDIDO
Quando receber um pedido:

1. **EXTRAIR:** Mesa/Balcão, Itens, Quantidade, Observações
2. **VALIDAR:** Confirme se entendeu corretamente
3. **ESTRUTURAR:** Responda no formato exato abaixo
4. **PEDIR CONFIRMAÇÃO:** Sempre pergunte antes de finalizar

## FORMATO DE RESPOSTA
Responda EXATAMENTE assim:

📋 **PEDIDO CONFIRMADO**
Mesa: [número]
Itens:
- [Item] (qtd: [n])
- [Item] (qtd: [n])
Observações: [texto ou "nenhuma"]

Está correto? Responda com:
✅ Confirmar
✏️ Alterar
❌ Cancelar

## CONTEXTO DO RESTAURANTE

**Projeto:** ${pd?.name || 'Restaurante'}

**Integrantes (WhatsApp):**
${team || '(não informado)'}

**Informações da Casa:**
${houseInfo || '(não informado)'}

## REGRAS IMPORTANTES
- Sempre peça confirmação antes de finalizar
- Se disser "Cancelar", descarte o pedido
- Se disser "Alterar", peça para informar o que mudar
- Mantenha um tom profissional e amigável
- Responda rapidamente
- Não invente itens que não foram mencionados

---
**Nota:** Esta é uma conversa normal que aparecerá em Atendimentos e será salva como histórico.`;
  };

  const handleStepChange = async (newStep) => {
    const prevStep = prevStepRef.current;

    const projectType = String(projectData?.project_type || '').trim();

    if (projectType === 'bar_restaurant') {
      // Ordem atual do wizard (bar_restaurant):
      // 1 Básico, 2 Membros, 3 Tipo, 4 Integrações, 5 Subtipo, 6 Integrantes, 7 Info Casa, 8 Revisão Agente, 9 Personalização, 10 Resumo
      if (prevStep === 7 && newStep === 8) {
        const currentPrompt = String(projectData?.barRestaurantAgentDraft?.prompt || '').trim();
        const currentAdminPrompt = String(projectData?.barRestaurantAdminAgentDraft?.prompt || '').trim();
        
        if (!currentPrompt) {
          console.log('[CreateProjectModal] Gerando prompt para Atendente do Salão...');
          const generatedPrompt = await generateBarRestaurantPrompt(projectData);
          setProjectData((prev) => ({
            ...prev,
            barRestaurantAgentDraft: {
              ...(prev.barRestaurantAgentDraft || {}),
              prompt: generatedPrompt,
            },
          }));
        }

        if (!currentAdminPrompt) {
          console.log('[CreateProjectModal] Gerando prompt para Agente Admin...');
          const generatedAdminPrompt = await generateBarRestaurantAdminPrompt(projectData);
          setProjectData((prev) => ({
            ...prev,
            barRestaurantAdminAgentDraft: {
              ...(prev.barRestaurantAdminAgentDraft || {}),
              prompt: generatedAdminPrompt,
            },
          }));
        }
      }
    }

    prevStepRef.current = newStep;
  };

  // Carregar conexões e agentes quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      loadConnections();
      loadAgents();
    }
  }, [isOpen]);

  // Carregar categorias de produtos quando for gas_distributor
  useEffect(() => {
    if (isOpen && getProjectType(projectData) === 'gas_distributor') {
      fetchProductCategories();
    }
  }, [isOpen, projectData]);

  const loadConnections = async () => {
    try {
      setLoadingIntegrations(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      const { data, error } = await supabase
        .from('connections')
        .select('id, name, type, status, agente_id')
        .eq('company_id', user.company_id)
        .order('name');

      if (error) throw error;

      // Mapear para formato do componente
      const mappedConnections = (data || []).map(conn => ({
        id: conn.id,
        name: conn.name,
        icon: conn.type === 'whatsapp' ? 'chat' : 'send',
        status: conn.status === 'CONECTADO' ? 'active' : 'inactive',
        color: conn.type === 'whatsapp' ? '#25D366' : '#0088cc',
        type: conn.type,
        agente_id: conn.agente_id
      }));

      setConnections(mappedConnections);
      console.log('✅ Conexões carregadas:', mappedConnections.length);
    } catch (error) {
      console.error('❌ Erro ao carregar conexões:', error);
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const loadAgents = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      const { data, error } = await supabase
        .from('agentes')
        .select('id, nome, funcao_principal, chatbot_enabled')
        .eq('empresa_id', user.company_id)
        .order('nome');

      if (error) throw error;

      // Mapear para formato do componente
      const mappedAgents = (data || []).map(agent => ({
        id: agent.id,
        name: agent.nome,
        icon: 'smart_toy',
        status: agent.chatbot_enabled ? 'active' : 'inactive',
        color: '#44e1ff',
        function: agent.funcao_principal
      }));

      setAgents(mappedAgents);
    } catch (error) {
      console.error('❌ Erro ao carregar agentes:', error);
    }
  };

  // Buscar detalhes da conexão (foto + nome do WhatsApp)
  const fetchConnectionDetails = async (connectionId) => {
    try {
      setLoadingConnectionDetails(true);
      
      const url = `${BACKEND_NODE_URL}/connections/${connectionId}/details`;
      console.log('[GasDistributor] Buscando detalhes da conexão:', { connectionId, url });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      console.log('[GasDistributor] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GasDistributor] Erro na resposta:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('[GasDistributor] Detalhes recebidos:', data);
      
      // A resposta vem estruturada como { details: {...} }
      const details = data.details || data;
      console.log('[GasDistributor] Detalhes extraídos:', details);
      
      setSelectedConnectionDetails(details);
      setTempConnectionName(details.profile_name || details.name || '');
      
      // Buscar grupos disponíveis
      await fetchAvailableGroups(connectionId);
    } catch (error) {
      console.error('❌ Erro ao buscar detalhes da conexão:', error);
      alert(`Erro ao carregar detalhes: ${error.message}`);
    } finally {
      setLoadingConnectionDetails(false);
    }
  };

  // Buscar grupos disponíveis da conexão
  const fetchAvailableGroups = async (connectionId) => {
    try {
      setLoadingGroups(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      
      const response = await fetch(`${BACKEND_NODE_URL}/connections/groups/${connectionId}?force=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token || ''}`
        }
      });

      if (!response.ok) throw new Error('Erro ao buscar grupos');
      
      const data = await response.json();
      setAvailableGroups(data.groups || []);
    } catch (error) {
      console.error('❌ Erro ao buscar grupos:', error);
      setAvailableGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Buscar categorias de produtos
  const fetchProductCategories = async () => {
    try {
      setLoadingCategories(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      
      const { data, error } = await supabase
        .from('categorias_produtos')
        .select('id, nome, descricao')
        .eq('company_id', user.company_id)
        .eq('ativo', true)
        .order('nome');

      if (error) throw error;

      setProductCategories(data || []);
      console.log('✅ Categorias carregadas:', data?.length || 0);
    } catch (error) {
      console.error('❌ Erro ao buscar categorias:', error);
      setProductCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Buscar produtos de uma categoria
  const fetchProductsByCategory = async (categoryId) => {
    try {
      setLoadingProducts(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      
      const { data, error } = await supabase
        .from('produtos_empresa')
        .select('id, nome, descricao, preco_venda, categoria_id')
        .eq('company_id', user.company_id)
        .eq('categoria_id', categoryId)
        .is('deleted_at', null)
        .order('nome');

      if (error) throw error;

      setCategoryProducts(data || []);
      console.log('✅ Produtos carregados:', data?.length || 0);
    } catch (error) {
      console.error('❌ Erro ao buscar produtos:', error);
      setCategoryProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Tipos de projeto
  const projectTypes = [
    { value: 'custom', label: 'Personalizado', icon: 'dashboard', description: 'Crie um projeto do zero com total flexibilidade' },
    { value: 'crm', label: 'CRM', icon: 'people', description: 'Gerencie relacionamento com clientes e leads' },
    { value: 'marketing_campaign', label: 'Campanha de marketing', icon: 'campaign', description: 'Kanban estilo CRM para campanhas e disparos' },
    { value: 'crm_multi', label: 'CRM Multi Atendimento', icon: 'groups', description: 'CRM com múltiplas conexões WhatsApp' },
    { value: 'tasks', label: 'Tarefas', icon: 'task_alt', description: 'Organize e acompanhe tarefas da equipe' },
    { value: 'support', label: 'Suporte', icon: 'support_agent', description: 'Gerencie tickets e atendimentos' },
    { value: 'bar_restaurant', label: 'Bar & Restaurante', icon: 'restaurant', description: 'Gestão de atendimento, salão, cozinha e bar' },
    { value: 'gas_distributor', label: 'Distribuidora de Gás', icon: 'local_shipping', description: 'Gestão de leads, atendimento, pedidos e entregas' },
    { value: 'financial', label: 'Financeiro', icon: 'payments', description: 'Controle financeiro e pagamentos' },
    { value: 'hr', label: 'RH', icon: 'badge', description: 'Gestão de pessoas e recursos humanos' },
    { value: 'inventory', label: 'Estoque', icon: 'inventory_2', description: 'Controle de estoque e produtos' }
  ];

  const handleAddBarRestaurantTeamMember = () => {
    const currentTeam = projectData.barRestaurantTeam || [];
    const nextId = Math.max(0, ...currentTeam.map(m => Number(m.id) || 0)) + 1;
    setProjectData({
      ...projectData,
      barRestaurantTeam: [...currentTeam, { id: nextId, name: '', phone: '' }]
    });
  };

  const handleRemoveBarRestaurantTeamMember = (memberId) => {
    const currentTeam = projectData.barRestaurantTeam || [];
    const nextTeam = currentTeam.filter(m => m.id !== memberId);
    setProjectData({
      ...projectData,
      barRestaurantTeam: nextTeam.length > 0 ? nextTeam : [{ id: 1, name: '', phone: '' }]
    });
  };

  const handleUpdateBarRestaurantTeamMember = (memberId, patch) => {
    const currentTeam = projectData.barRestaurantTeam || [];
    setProjectData({
      ...projectData,
      barRestaurantTeam: currentTeam.map(m => (m.id === memberId ? { ...m, ...patch } : m))
    });
  };

  const handleBarRestaurantFilesChange = async (e) => {
    const files = Array.from(e?.target?.files || []);
    setProjectData({
      ...projectData,
      barRestaurantFiles: files
    });

    // Processar e extrair texto do arquivo
    if (files.length > 0) {
      try {
        const file = files[0]; // Pegar primeiro arquivo
        console.log(`[CreateProjectModal] Processando arquivo: ${file.name} (${file.type})`);

        // Criar FormData para enviar ao backend
        const formData = new FormData();
        formData.append('file', file);

        // Enviar para backend extrair texto
        const response = await fetch(`${BACKEND_NODE_URL}/api/bar-restaurant/extract-file`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Erro ao extrair arquivo');
        }

        // Atualizar o campo de texto extraído
        setProjectData(prev => ({
          ...prev,
          barRestaurantExtractedText: result.extractedText
        }));

        console.log(`[CreateProjectModal] ✅ Arquivo processado: ${file.name} (${result.extractedText.length} caracteres)`);
      } catch (err) {
        console.error('[CreateProjectModal] Erro ao processar arquivo:', err.message);
        setProjectData(prev => ({
          ...prev,
          barRestaurantExtractedText: `[Erro ao processar arquivo: ${err.message}]`
        }));
      }
    }
  };

  // Cores disponíveis
  const availableColors = [
    { color: '#6366f1', name: 'Índigo' },
    { color: '#8b5cf6', name: 'Roxo' },
    { color: '#ec4899', name: 'Rosa' },
    { color: '#ef4444', name: 'Vermelho' },
    { color: '#f59e0b', name: 'Laranja' },
    { color: '#10b981', name: 'Verde' },
    { color: '#06b6d4', name: 'Ciano' },
    { color: '#3b82f6', name: 'Azul' }
  ];


  const processImageFile = (file) => {
    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem.');
      return;
    }
    
    // Validar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5MB.');
      return;
    }

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProjectData({
        ...projectData,
        backgroundImage: file,
        backgroundImagePreview: reader.result
      });
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImageFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file) {
      processImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setProjectData({
      ...projectData,
      backgroundImage: null,
      backgroundImagePreview: null
    });
  };

  const handleToggleAllUsers = () => {
    setProjectData({
      ...projectData,
      allUsersSelected: !projectData.allUsersSelected,
      members: []
    });
  };

  const handleToggleUser = (userId) => {
    const currentMembers = projectData.members || [];
    
    if (currentMembers.includes(userId)) {
      setProjectData({
        ...projectData,
        allUsersSelected: false,
        members: currentMembers.filter(id => id !== userId)
      });
    } else {
      setProjectData({
        ...projectData,
        allUsersSelected: false,
        members: [...currentMembers, userId]
      });
    }
  };

  const handleToggleConnection = (connectionId) => {
    const currentConnections = projectData.connections || [];
    
    // Se for CRM tradicional, permitir apenas 1 conexão WhatsApp
    // CRM Multi permite múltiplas conexões
    const projectType = getProjectType(projectData);
    if (projectType === 'crm') {
      const connection = connections.find(c => c.id === connectionId);
      
      if (connection?.type !== 'whatsapp') {
        alert('⚠️ Projetos CRM só podem usar conexões WhatsApp');
        return;
      }
      
      // Se já tem uma conexão, substituir
      if (currentConnections.length > 0 && !currentConnections.includes(connectionId)) {
        setProjectData({
          ...projectData,
          connections: [connectionId]
        });
        return;
      }
    }
    
    // CRM Multi e outros tipos: permitir múltiplas conexões
    if (projectType === 'crm_multi') {
      const connection = connections.find(c => c.id === connectionId);
      if (connection?.type !== 'whatsapp') {
        alert('⚠️ Projetos CRM Multi só podem usar conexões WhatsApp');
        return;
      }
    }
    
    if (currentConnections.includes(connectionId)) {
      setProjectData({
        ...projectData,
        connections: currentConnections.filter(id => id !== connectionId)
      });
    } else {
      setProjectData({
        ...projectData,
        connections: [...currentConnections, connectionId]
      });
    }
  };

  const handleToggleAgent = (agentId) => {
    const currentAgents = projectData.agents || [];
    if (currentAgents.includes(agentId)) {
      setProjectData({
        ...projectData,
        agents: currentAgents.filter(id => id !== agentId)
      });
    } else {
      setProjectData({
        ...projectData,
        agents: [...currentAgents, agentId]
      });
    }
  };

  // Funções para gerenciar campos do formulário
  const handleEditField = (field) => {
    setEditingField(field.id);
    setEditFieldName(field.name);
    setEditFieldRequired(field.required);
  };

  const handleSaveField = () => {
    setProjectData({
      ...projectData,
      formFields: projectData.formFields.map(field => 
        field.id === editingField 
          ? { ...field, name: editFieldName, required: editFieldRequired }
          : field
      )
    });
    setEditingField(null);
    setEditFieldName('');
    setEditFieldRequired(false);
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditFieldName('');
    setEditFieldRequired(false);
  };

  const handleDeleteField = (fieldId) => {
    if (confirm('Tem certeza que deseja remover este campo?')) {
      setProjectData({
        ...projectData,
        formFields: projectData.formFields.filter(field => field.id !== fieldId)
      });
    }
  };

  const handleAddField = () => {
    if (!newFieldName.trim()) {
      alert('Por favor, digite o nome do campo');
      return;
    }

    const newField = {
      id: Date.now(),
      name: newFieldName,
      icon: newFieldIcon,
      required: newFieldRequired
    };

    setProjectData({
      ...projectData,
      formFields: [...projectData.formFields, newField]
    });

    // Resetar modal
    setShowAddFieldModal(false);
    setNewFieldName('');
    setNewFieldIcon('label');
    setNewFieldRequired(false);
  };

  // Validação customizada antes de avançar steps
  const validateBeforeNext = (currentStepIndex) => {
    const projectType = getProjectType(projectData);
    
    // Step 4 (Integrações) - índice 3 (0-based)
    if (currentStepIndex === 3) {
      // Para CRM, CRM Multi e Bar & Restaurante
      if (projectType === 'crm' || projectType === 'crm_multi' || projectType === 'bar_restaurant') {
        const whatsappConnections = connections.filter(c => c.type === 'whatsapp');
        const hasWhatsAppSelected = projectData.connections.some(id => 
          whatsappConnections.find(c => c.id === id)
        );
        
        if (!hasWhatsAppSelected) {
          const msg = projectType === 'crm_multi' 
            ? '⚠️ É obrigatório selecionar pelo menos uma conexão WhatsApp!'
            : '⚠️ É obrigatório selecionar uma conexão WhatsApp!';
          alert(msg);
          return false;
        }
      }
      
      // Para Gas Distributor
      if (projectType === 'gas_distributor') {
        // Validar se selecionou conexão
        if (!projectData.connections || projectData.connections.length === 0) {
          alert('⚠️ É obrigatório selecionar uma conexão WhatsApp!');
          return false;
        }
        
        // Validar se selecionou grupo
        if (!projectData.grupoWhatsappId) {
          alert('⚠️ É obrigatório selecionar um grupo WhatsApp!');
          return false;
        }
      }
    }
    
    return true;
  };

  // Função para montar o prompt do agente Gas Distributor
  const generateGasDistributorPrompt = () => {
    // Formatar horários
    const horariosFormatados = Object.entries(horariosFuncionamento)
      .filter(([_, info]) => info.ativo)
      .map(([dia, info]) => `${dia.charAt(0).toUpperCase() + dia.slice(1)}: ${info.abertura} - ${info.fechamento}`)
      .join('\n');

    // Formatar áreas de cobertura
    const areasFormatadas = areasCobertura.length > 0
      ? areasCobertura.map(area => `- ${area.nome}: R$ ${area.taxa} (${area.tempo} minutos)`)
        .join('\n')
      : 'Sem áreas configuradas';

    // Formatar formas de pagamento
    const formasPagamentoFormatadas = formasPagamentoEstruturado
      .filter(f => f.ativo)
      .map(f => {
        if (f.tipo === 'dinheiro') return 'Dinheiro';
        if (f.tipo === 'pix') return 'PIX';
        if (f.tipo === 'cartao') return 'Cartão';
        if (f.tipo === 'outros') return f.nome || 'Outros';
        return f.tipo;
      })
      .join(', ');

    // Formatar produtos
    const produtosFormatados = projectData.produtosDisponiveis && projectData.produtosDisponiveis.length > 0
      ? projectData.produtosDisponiveis.map(p => `- ${p.nome}: R$ ${p.preco_venda}`).join('\n')
      : 'Nenhum produto configurado';

    const prompt = `VOCÊ É UM AGENTE DE ATENDIMENTO AUTOMÁTICO PARA ${projectData.name?.toUpperCase() || 'DISTRIBUIDORA DE GÁS'}

INFORMAÇÕES DA EMPRESA:
- Nome: ${projectData.name}
- Descrição: ${projectData.description}
- Endereço Base: ${projectData.enderecoBase}
- Tempo Médio de Entrega: ${tempoMedioEntrega} minutos

HORÁRIOS DE FUNCIONAMENTO:
${horariosFormatados}

ÁREAS DE COBERTURA E TAXAS DE ENTREGA:
${areasFormatadas}

FORMAS DE PAGAMENTO ACEITAS:
${formasPagamentoFormatadas}

PRODUTOS DISPONÍVEIS:
${produtosFormatados}

POLÍTICA DE ENTREGA:
${politicaEntrega || 'Não configurada'}

POLÍTICA DE CANCELAMENTO:
${politicaCancelamento || 'Não configurada'}

SEU OBJETIVO:
Atender clientes de forma amigável, coletar informações, confirmar pedidos e criar cards no kanban.

FLUXO DE ATENDIMENTO:

1. CLIENTE NOVO (não existe em cliente_distribuidor):
   a) Saudação: "Olá! Bem-vindo à ${projectData.name}! 👋"
   b) Perguntar: "Qual é seu nome?"
   c) Perguntar: "Qual é seu endereço de entrega?"
   d) Criar novo registro em cliente_distribuidor
   e) Ir para PASSO 3

2. CLIENTE EXISTENTE (já existe em cliente_distribuidor):
   a) Buscar dados do cliente
   b) Saudação: "Olá [NOME]! Bem-vindo de volta! 👋"
   c) Ler observações para saber como atender
   d) Ir para PASSO 3

3. PERGUNTA O QUE CLIENTE DESEJA:
   a) "O que você deseja hoje?"
   b) Cliente responde (ex: "Quero pedir gás")
   c) Ir para PASSO 4

4. CLIENTE FAZ PEDIDO:
   a) Buscar produtos disponíveis
   b) Perguntar: "Qual produto você deseja?"
   c) Perguntar: "Quantas unidades?"
   d) Ir para PASSO 5

5. CONFIRMAÇÃO DE ENDEREÇO:
   a) Buscar endereço principal do cliente
   b) Perguntar: "Vai ser entregue em [ENDEREÇO_PRINCIPAL]?"
   c) Se NÃO: Mostrar outros endereços cadastrados
   d) Se SIM: Continuar
   e) Ir para PASSO 6

6. CONFIRMAÇÃO DE PREFERÊNCIAS:
   a) Buscar histórico de compras
   b) Perguntar: "Forma de pagamento: [FORMA_PREFERIDA] como sempre?"
   c) Perguntar: "Horário preferido: [HORÁRIO_PREFERIDO]?"
   d) Se cliente tem histórico: "Quer agendar como da última vez?"
   e) Ir para PASSO 7

7. CÁLCULO DE PREÇO:
   a) Buscar preço do produto
   b) Buscar tags de desconto do cliente (VIP, Recorrente, Novo)
   c) Calcular preço com desconto
   d) Ir para PASSO 8

8. CONFIRMAÇÃO FINAL:
   a) Resumir pedido:
      "Perfeito! Vou confirmar seu pedido:
      - Produto: [PRODUTO]
      - Quantidade: [QTD]
      - Endereço: [ENDEREÇO]
      - Forma de Pagamento: [FORMA]
      - Horário: [HORÁRIO]
      - Preço: R$ [PREÇO] (desconto [DESCONTO]% aplicado!)
      
      Está tudo certo?"
   
   b) Se cliente confirma (SIM):
      - Criar pedido no kanban (lista "Pedido Confirmado - Entrega Imediata" ou "Agendada")
      - Atualizar cliente_distribuidor
      - Enviar mensagem no grupo de entregadores
      - Mensagem para cliente: "Seu pedido foi confirmado! Saiu para entrega agora! 🚗"
      - FIM
   
   c) Se cliente não confirma (NÃO):
      - Perguntar: "O que você gostaria de alterar?"
      - Voltar para PASSO 4

FERRAMENTAS QUE VOCÊ USA:
1. buscar_cliente_distribuidor(telefone) → Busca cliente existente
2. criar_cliente_distribuidor(nome, telefone, endereço) → Cria novo cliente
3. buscar_endereco_cliente(cliente_id) → Lista endereços do cliente
4. adicionar_endereco_cliente(cliente_id, endereço) → Adiciona novo endereço
5. buscar_historico_compras(cliente_id) → Mostra compras anteriores
6. consultar_produtos() → Lista produtos disponíveis
7. buscar_preco_com_desconto(produto_id, cliente_id) → Calcula preço com desconto
8. criar_pedido_gas_distributor(cliente_id, produto_id, qtd, endereço, forma_pagamento, horário) → Cria card no kanban

REGRAS IMPORTANTES:
- Sempre ser amigável e profissional
- Confirmar tudo antes de criar o pedido
- Se cliente não responder em 5 minutos, enviar lembrete
- Se cliente cancelar, mover para lista "Cancelado"
- Sempre ler observações do cliente para personalizar atendimento
- Se cliente tem desconto VIP, sempre mencionar
- Se cliente é novo, enviar imagem de boas-vindas`;

    return prompt;
  };

  const handleComplete = async () => {
    try {
      // Validação final para CRM
      const projectType = getProjectType(projectData);
      if (projectType === 'crm' || projectType === 'bar_restaurant') {
        const whatsappConnections = connections.filter(c => c.type === 'whatsapp');
        const hasWhatsAppSelected = projectData.connections.some(id => 
          whatsappConnections.find(c => c.id === id)
        );
        
        if (!hasWhatsAppSelected) {
          alert('⚠️ É obrigatório selecionar uma conexão WhatsApp!');
          return;
        }
      }
      
      // Se houver imagem, fazer upload primeiro
      let backgroundImageUrl = projectData.backgroundImageUrl || null;
      
      // Só fazer upload se for uma nova imagem (File object)
      if (projectData.backgroundImage && projectData.backgroundImage instanceof File) {
        const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
        const fileExt = projectData.backgroundImage.name.split('.').pop();
        const fileName = `${user.company_id}/${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('project-backgrounds')
          .upload(fileName, projectData.backgroundImage, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Erro ao fazer upload da imagem:', uploadError);
          alert('Erro ao fazer upload da imagem. O projeto será salvo sem nova imagem de fundo.');
        } else {
          // Obter URL pública da imagem
          const { data: urlData } = supabase.storage
            .from('project-backgrounds')
            .getPublicUrl(fileName);
          
          backgroundImageUrl = urlData.publicUrl;
        }
      }
      
      // Para Bar & Restaurante, salvar agente e prompt no backend
      if (projectType === 'bar_restaurant') {
        console.log('[CreateProjectModal] Salvando projeto Bar & Restaurante...');
        
        // Primeiro, criar o projeto normalmente
        let projectResult;
        try {
          projectResult = await onCreate({
            ...projectData,
            backgroundImageUrl
          });
        } catch (err) {
          console.error('[CreateProjectModal] Erro ao criar projeto:', err);
          throw err;
        }

        if (!projectResult || !projectResult.id) {
          console.error('[CreateProjectModal] Projeto criado mas sem ID:', projectResult);
          alert('⚠️ Projeto criado, mas não foi possível obter o ID para salvar o agente.');
          return;
        }

        console.log('[CreateProjectModal] Projeto criado com ID:', projectResult.id);

        // Aguardar um pouco para o projeto ser criado
        await new Promise(resolve => setTimeout(resolve, 500));

        // Depois, salvar agente e prompt
        const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
        
        try {
          console.log('[CreateProjectModal] Enviando dados para /save-project:', {
            projectId: projectResult.id,
            projectName: projectData.name,
            agentPrompt: projectData.barRestaurantAgentDraft?.prompt?.substring(0, 50) + '...'
          });

          const saveResponse = await fetch(`${BACKEND_NODE_URL}/api/bar-restaurant/save-project`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${user.access_token || ''}`
            },
            body: JSON.stringify({
              projectId: projectResult.id,
              projectName: projectData.name,
              projectDescription: projectData.description,
              barRestaurantTeam: projectData.barRestaurantTeam,
              barRestaurantExtractedText: projectData.barRestaurantExtractedText,
              barRestaurantHouseInfo: projectData.barRestaurantHouseInfo,
              agentPrompt: projectData.barRestaurantAgentDraft?.prompt || '',
              adminAgentPrompt: projectData.barRestaurantAdminAgentDraft?.prompt || '',
              connectionId: projectData.connections[0] || null
            })
          });

          if (!saveResponse.ok) {
            const error = await saveResponse.json();
            console.error('[CreateProjectModal] Erro ao salvar projeto:', error);
            alert('⚠️ Projeto criado, mas houve erro ao salvar o agente: ' + error.error);
          } else {
            const result = await saveResponse.json();
            console.log('[CreateProjectModal] ✅ Projeto salvo com sucesso:', result);
            alert('✅ Projeto e agente criados com sucesso!');
          }
        } catch (err) {
          console.error('[CreateProjectModal] Erro ao chamar save-project:', err.message);
          alert('⚠️ Projeto criado, mas houve erro ao salvar o agente: ' + err.message);
        }
      } else if (projectType === 'gas_distributor') {
        console.log('[CreateProjectModal] Salvando projeto Gas Distributor...');
        
        // Primeiro, criar o projeto normalmente
        let projectResult;
        try {
          projectResult = await onCreate({
            ...projectData,
            backgroundImageUrl
          });
        } catch (err) {
          console.error('[CreateProjectModal] Erro ao criar projeto:', err);
          throw err;
        }

        if (!projectResult || !projectResult.id) {
          console.error('[CreateProjectModal] Projeto criado mas sem ID:', projectResult);
          alert('⚠️ Projeto criado, mas não foi possível obter o ID para salvar o agente.');
          return;
        }

        console.log('[CreateProjectModal] Projeto criado com ID:', projectResult.id);

        // Aguardar um pouco para o projeto ser criado
        await new Promise(resolve => setTimeout(resolve, 500));

        // Depois, salvar agente e criar listas do kanban
        const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
        const agentPrompt = generateGasDistributorPrompt();
        
        try {
          console.log('[CreateProjectModal] Enviando dados para /api/gas-distributor/save-project');

          const saveResponse = await fetch(`${BACKEND_NODE_URL}/api/gas-distributor/save-project`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${user.access_token || ''}`
            },
            body: JSON.stringify({
              projectId: projectResult.id,
              projectName: projectData.name,
              projectDescription: projectData.description,
              connectionId: projectData.connections[0] || null,
              grupoWhatsappId: projectData.grupoWhatsappId,
              grupoWhatsappNome: projectData.grupoWhatsappNome,
              enderecoBaseEstruturado: enderecoBaseEstruturado,
              tempoMedioEntrega: tempoMedioEntrega,
              horariosFuncionamento: horariosFuncionamento,
              formasPagamentoEstruturado: formasPagamentoEstruturado,
              areasCobertura: areasCobertura,
              politicaEntrega: politicaEntrega,
              politicaCancelamento: politicaCancelamento,
              politicaVendas: politicaVendas,
              produtosDisponiveis: projectData.produtosDisponiveis || [],
              agentPrompt: agentPrompt,
              team: projectData.team || [],
              companyDataEtapa6: companyDataEtapa6 || {}
            })
          });

          if (!saveResponse.ok) {
            const error = await saveResponse.json();
            console.error('[CreateProjectModal] Erro ao salvar projeto:', error);
            alert('⚠️ Projeto criado, mas houve erro ao salvar o agente: ' + error.error);
          } else {
            const result = await saveResponse.json();
            console.log('[CreateProjectModal] ✅ Projeto salvo com sucesso:', result);
            alert('✅ Projeto Gas Distributor e agente criados com sucesso!');
          }
        } catch (err) {
          console.error('[CreateProjectModal] Erro ao chamar save-project:', err.message);
          alert('⚠️ Projeto criado, mas houve erro ao salvar o agente: ' + err.message);
        }
      } else {
        // Para outros tipos de projeto, usar o fluxo normal
        onCreate({
          ...projectData,
          backgroundImageUrl
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Erro ao processar projeto:', error);
      alert('Erro ao processar projeto: ' + error.message);
    }
  };

  if (!isOpen) return null;

  const projectType = getProjectType(projectData);

  return (
    <div className="modal-overlay-stepper" onClick={onClose}>
      <div className="modal-content-stepper" onClick={(e) => e.stopPropagation()}>
        <button className="close-button-stepper" onClick={onClose}>
          <AppIcon name="close" />
        </button>

        <Stepper
          initialStep={1}
          onFinalStepCompleted={handleComplete}
          onStepChange={handleStepChange}
          backButtonText="Voltar"
          nextButtonText="Continuar"
        >
          {/* STEP 1: Informações Básicas */}
          <Step>
            <div className="step-header">
              <AppIcon name="info" className="step-icon" />
              <h2>Informações Básicas</h2>
              <p>Vamos começar com o nome e descrição do seu projeto</p>
              <div className="ai-helper-info">
                <AppIcon name="smart_toy" />
                <p>
                  <strong>💡 Dica HALO:</strong> Quanto mais detalhes você fornecer, melhor! 
                  Nosso agente de IA HALO usará essas informações para criar automaticamente 
                  listas, tarefas e estruturas personalizadas para o seu projeto.
                </p>
              </div>
            </div>

            <div className="step-content">
              <div className="form-field-step">
                <label>
                  <AppIcon name="label" />
                  Nome do Projeto *
                </label>
                <input
                  type="text"
                  placeholder="Ex: Pipeline de Vendas 2024"
                  value={projectData.name}
                  onChange={(e) => setProjectData({...projectData, name: e.target.value})}
                  className="input-step"
                />
                <span className="field-hint">Escolha um nome claro e descritivo</span>
              </div>

              <div className="form-field-step">
                <label>
                  <AppIcon name="description" />
                  Descrição
                </label>
                <textarea
                  placeholder="Descreva o objetivo e escopo deste projeto..."
                  value={projectData.description}
                  onChange={(e) => setProjectData({...projectData, description: e.target.value})}
                  rows={4}
                  className="textarea-step"
                />
                <span className="field-hint">Ajude sua equipe a entender o propósito do projeto</span>
              </div>
            </div>
          </Step>

          {/* STEP 2: Membros da Equipe */}
          <Step>
            <div className="step-header">
              <AppIcon name="group" className="step-icon" />
              <h2>Membros da Equipe</h2>
              <p>Defina quem terá acesso a este projeto</p>
            </div>

            <div className="step-content">
              <div className="members-selector-step">
                <div 
                  className={`member-option-step ${projectData.allUsersSelected ? 'selected' : ''}`}
                  onClick={handleToggleAllUsers}
                >
                  <AppIcon name={projectData.allUsersSelected ? 'check_circle' : 'radio_button_unchecked'} />
                  <div className="member-option-content">
                    <span className="member-option-title">Toda equipe</span>
                    <span className="member-option-desc">Todos os usuários da empresa terão acesso</span>
                  </div>
                </div>
                
                {!projectData.allUsersSelected && (
                  <div className="users-list-step">
                    <div className="users-list-header">
                      <AppIcon name="person_search" />
                      <span>Selecione os membros específicos:</span>
                    </div>
                    {users.map(user => (
                      <div
                        key={user.id}
                        className={`user-option-step ${projectData.members.includes(user.id) ? 'selected' : ''}`}
                        onClick={() => handleToggleUser(user.id)}
                      >
                        <AppIcon name={projectData.members.includes(user.id) ? 'check_box' : 'check_box_outline_blank'} />
                        <div className="user-info-step">
                          <span className="user-name-step">{user.nome}</span>
                          <span className="user-email-step">{user.email}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Step>

          {/* STEP 3: Tipo de Projeto */}
          <Step>
            <div className="step-header">
              <AppIcon name="category" className="step-icon" />
              <h2>Modelo/Template do Projeto</h2>
              <p>Escolha o modelo que melhor se adapta ao seu projeto</p>
              <div className="ai-helper-info">
                <AppIcon name="auto_awesome" />
                <p>
                  <strong>✨ Geração Inteligente:</strong> Com base no modelo escolhido, 
                  o agente HALO irá gerar automaticamente as próximas etapas de configuração, 
                  incluindo listas, fluxos de trabalho e estruturas personalizadas para o seu tipo de projeto.
                </p>
              </div>
            </div>

            <div className="step-content">
              <div className="project-types-grid-step">
                {projectTypes.map(type => (
                  <div
                    key={type.value}
                    className={`project-type-card-step ${projectType === type.value ? 'selected' : ''}`}
                    onClick={() => setProjectData({...projectData, project_type: type.value, icon: type.icon})}
                  >
                    <AppIcon name={type.icon} className="type-icon" />
                    <span className="type-label">{type.label}</span>
                    <span className="type-description">{type.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </Step>

          {/* Campanha de marketing: etapa provisória antes das integrações */}
          {projectType === 'marketing_campaign' && (
            <Step>
              <div className="step-header">
                <AppIcon name="campaign" className="step-icon" />
                <h2>Configuração da campanha</h2>
                <p>Estamos preparando as opções específicas deste modelo</p>
              </div>
              <div className="step-content">
                <div className="form-info-box" style={{ marginTop: '0.5rem' }}>
                  <AppIcon name="construction" />
                  <div>
                    <p style={{ margin: 0, lineHeight: 1.5 }}>
                      A configuração dinâmica deste tipo de projeto será implementada em breve.
                      Por enquanto, você pode continuar, revisar os dados básicos e criar o projeto;
                      o quadro virá com as listas padrão (incluindo <strong>Disparo pendente</strong> e{' '}
                      <strong>Disparo efetivado</strong> antes de <strong>Em atendimento</strong>).
                    </p>
                  </div>
                </div>
              </div>
            </Step>
          )}

          {/* STEP 4: Gas Distributor - Conexão e Grupo */}
          {projectType === 'gas_distributor' && (
            <Step>
              <div className="step-header">
                <AppIcon name="link" className="step-icon" />
                <h2>Conexão WhatsApp e Grupo</h2>
                <p>Selecione a conexão WhatsApp e configure o grupo para notificações</p>
              </div>

              <div className="step-content">
                {/* Seleção de Conexão */}
                {!selectedConnectionDetails ? (
                  <div className="form-field-step">
                    <label>
                      <AppIcon name="link" />
                      Selecionar Conexão WhatsApp
                    </label>
                    <p className="field-hint">Escolha a conexão WhatsApp que será usada para este projeto</p>
                    
                    {loadingIntegrations ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                        <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem' }} />
                        <p>Carregando conexões...</p>
                      </div>
                    ) : connections.filter(conn => conn.type === 'whatsapp' && conn.status === 'active' && !conn.agente_id).length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                        <AppIcon name="link_off" style={{ fontSize: '2rem' }} />
                        <p>Nenhuma conexão WhatsApp disponível</p>
                        <p style={{ fontSize: '12px', marginTop: '8px' }}>Conexões com agentes vinculados não podem ser usadas</p>
                      </div>
                    ) : (
                      <div className="integration-list">
                        {connections
                          .filter(conn => conn.type === 'whatsapp' && conn.status === 'active' && !conn.agente_id)
                          .map(connection => (
                            <div
                              key={connection.id}
                              className="integration-item"
                              onClick={() => {
                                setProjectData({...projectData, connections: [connection.id]});
                                fetchConnectionDetails(connection.id);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <AppIcon name={connection.icon} style={{ color: connection.color }} />
                              <div className="integration-info">
                                <span className="integration-name">{connection.name}</span>
                                <span className="integration-status">Disponível</span>
                              </div>
                              <AppIcon name="arrow_forward" />
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Detalhes da Conexão Selecionada - Sem Card */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="account_circle" />
                        Perfil WhatsApp Selecionado
                      </label>

                      {loadingConnectionDetails ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                          <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem' }} />
                          <p>Carregando informações do perfil...</p>
                        </div>
                      ) : selectedConnectionDetails ? (
                        <>
                          {/* Exibição Simples: Imagem + Nome */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                            {selectedConnectionDetails?.profile_pic_url ? (
                              <img 
                                src={selectedConnectionDetails.profile_pic_url} 
                                alt="Perfil WhatsApp" 
                                style={{ 
                                  width: '80px', 
                                  height: '80px', 
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '2px solid #e5e7eb'
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: '#f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid #e5e7eb'
                              }}>
                                <AppIcon name="account_circle" style={{ fontSize: '40px', color: '#9ca3af' }} />
                              </div>
                            )}
                            
                            <div>
                              <p style={{ margin: '0', fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Perfil WhatsApp</p>
                              <p style={{ margin: '4px 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                                {selectedConnectionDetails?.profile_name || selectedConnectionDetails?.name || 'Sem nome'}
                              </p>
                              <p style={{ margin: '0', fontSize: '13px', color: '#6b7280' }}>
                                {selectedConnectionDetails?.owner || 'Número não disponível'}
                              </p>
                            </div>
                          </div>

                          {/* Alterar Nome */}
                          {!editingConnectionName ? (
                            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>Nome do Perfil</p>
                                <button
                                  type="button"
                                  className="btn-icon-field"
                                  onClick={() => setEditingConnectionName(true)}
                                  title="Editar nome"
                                >
                                  <AppIcon name="edit" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>Novo Nome</p>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <input
                                  type="text"
                                  value={tempConnectionName}
                                  onChange={(e) => setTempConnectionName(e.target.value)}
                                  placeholder="Ex: Minha Empresa"
                                  className="input-step"
                                  style={{ flex: 1 }}
                                />
                                <button
                                  type="button"
                                  className="btn-save-field"
                                  onClick={async () => {
                                    if (!tempConnectionName.trim()) return;
                                    try {
                                      const user = JSON.parse(localStorage.getItem('astro_user_data'));
                                      const response = await fetch(`${BACKEND_NODE_URL}/connections/profile-name`, {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${user.access_token || ''}`
                                        },
                                        body: JSON.stringify({
                                          connection_id: projectData.connections[0],
                                          name: tempConnectionName.trim()
                                        })
                                      });
                                      if (response.ok) {
                                        setSelectedConnectionDetails({
                                          ...selectedConnectionDetails,
                                          profile_name: tempConnectionName.trim()
                                        });
                                        setEditingConnectionName(false);
                                      }
                                    } catch (error) {
                                      console.error('Erro ao alterar nome:', error);
                                    }
                                  }}
                                  title="Salvar"
                                >
                                  <AppIcon name="check" />
                                </button>
                                <button
                                  type="button"
                                  className="btn-icon-field delete"
                                  onClick={() => setEditingConnectionName(false)}
                                  title="Cancelar"
                                >
                                  <AppIcon name="close" />
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Alterar Imagem */}
                          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>Imagem do Perfil</p>
                              <button
                                type="button"
                                className="btn-icon-field"
                                onClick={() => {
                                  const input = document.createElement('input');
                                  input.type = 'file';
                                  input.accept = 'image/*';
                                  input.onchange = async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    
                                    try {
                                      const reader = new FileReader();
                                      reader.onload = async () => {
                                        try {
                                          const imageBase64 = reader.result;
                                          const user = JSON.parse(localStorage.getItem('astro_user_data'));
                                          
                                          const response = await fetch(`${BACKEND_NODE_URL}/connections/profile-image`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                              connection_id: projectData.connections[0],
                                              imageBase64
                                            })
                                          });
                                          
                                          if (response.ok) {
                                            // Recarregar detalhes da conexão
                                            await fetchConnectionDetails(projectData.connections[0]);
                                            alert('Imagem alterada com sucesso!');
                                          } else {
                                            alert('Erro ao alterar imagem');
                                          }
                                        } catch (error) {
                                          console.error('Erro ao alterar imagem:', error);
                                          alert('Erro ao alterar imagem');
                                        }
                                      };
                                      reader.onerror = () => {
                                        alert('Erro ao ler o arquivo');
                                      };
                                      reader.readAsDataURL(file);
                                    } catch (error) {
                                      console.error('Erro ao processar imagem:', error);
                                      alert('Erro ao processar imagem');
                                    }
                                  };
                                  input.click();
                                }}
                                title="Alterar imagem"
                              >
                                <AppIcon name="image" />
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                          <p>Nenhuma conexão selecionada</p>
                        </div>
                      )}
                    </div>

                    {/* Seleção de Grupo */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="group" />
                        Selecionar Grupo para Notificações
                      </label>
                      <p className="field-hint">Escolha um grupo existente ou crie um novo</p>

                      {loadingGroups ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                          <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem' }} />
                          <p>Carregando grupos...</p>
                        </div>
                      ) : availableGroups.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                          <AppIcon name="groups" style={{ fontSize: '2rem' }} />
                          <p>Nenhum grupo disponível</p>
                          <p style={{ fontSize: '12px', marginTop: '8px' }}>Crie um novo grupo abaixo</p>
                        </div>
                      ) : (
                        <div className="integration-list">
                          {availableGroups.map(group => (
                            <div
                              key={group.jid}
                              className={`integration-item ${projectData.grupoWhatsappId === group.jid ? 'selected' : ''}`}
                              onClick={() => setProjectData({
                                ...projectData, 
                                grupoWhatsappId: group.jid,
                                grupoWhatsappNome: group.name
                              })}
                              style={{ cursor: 'pointer' }}
                            >
                              <AppIcon name="groups" style={{ color: '#25D366' }} />
                              <div className="integration-info">
                                <span className="integration-name">{group.name}</span>
                                <span className="integration-status">
                                  {group.participant_count} participante{group.participant_count !== 1 ? 's' : ''}
                                </span>
                              </div>
                              {projectData.grupoWhatsappId === group.jid && (
                                <AppIcon name="check_circle" className="check-icon" />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Step>
          )}

          {/* STEP 4: Integrações (para outros tipos de projeto) */}
          {projectType !== 'gas_distributor' && (
            <Step>
              <div className="step-header">
                <AppIcon name="extension" className="step-icon" />
                <h2>Integrações</h2>
                <p>Conecte seu projeto com ferramentas e agentes de IA</p>
                {projectType === 'crm' && (
                  <div className="ai-helper-info" style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)' }}>
                    <AppIcon name="info" style={{ color: '#ef4444' }} />
                    <p>
                      <strong>⚠️ Importante:</strong> Para projetos CRM, é obrigatório selecionar 
                      <strong> uma conexão WhatsApp</strong>. Apenas uma conexão pode ser vinculada ao projeto.
                    </p>
                  </div>
                )}
                {projectType === 'bar_restaurant' && (
                  <div className="ai-helper-info" style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)' }}>
                    <AppIcon name="info" style={{ color: '#ef4444' }} />
                    <p>
                      <strong>⚠️ Importante:</strong> Para projetos Bar &amp; Restaurante, é obrigatório selecionar 
                      <strong> uma conexão WhatsApp</strong>. Ela será usada para criar e operar o grupo do projeto.
                    </p>
                  </div>
                )}
              </div>

            <div className="step-content">
              {/* Tabs */}
              <div className="integration-tabs">
                <button
                  className={`integration-tab ${activeIntegrationTab === 'connections' ? 'active' : ''}`}
                  onClick={() => setActiveIntegrationTab('connections')}
                >
                  <AppIcon name="link" />
                  Conexões
                </button>
                <button
                  className={`integration-tab ${activeIntegrationTab === 'agents' ? 'active' : ''}`}
                  onClick={() => setActiveIntegrationTab('agents')}
                >
                  <AppIcon name="smart_toy" />
                  Agentes IA
                </button>
              </div>

              {/* Lista de Conexões */}
              {activeIntegrationTab === 'connections' && (
                <div className="integration-list">
                  {loadingIntegrations ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                      <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem' }} />
                      <p>Carregando conexões...</p>
                    </div>
                  ) : connections.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                      <AppIcon name="link_off" style={{ fontSize: '2rem' }} />
                      <p>Nenhuma conexão configurada</p>
                    </div>
                  ) : (
                    connections
                      .filter(conn => projectType !== 'crm' || conn.type === 'whatsapp')
                      .map(connection => (
                    <div
                      key={connection.id}
                      className={`integration-item ${projectData.connections.includes(connection.id) ? 'selected' : ''}`}
                      onClick={() => connection.status === 'active' && handleToggleConnection(connection.id)}
                    >
                      <AppIcon name={connection.icon} style={{ color: connection.color }} />
                      <div className="integration-info">
                        <span className="integration-name">{connection.name}</span>
                        <span className="integration-status">
                          {connection.status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      {projectData.connections.includes(connection.id) && (
                        <AppIcon name="check_circle" className="check-icon" />
                      )}
                    </div>
                  )))}
                </div>
              )}

              {/* Lista de Agentes */}
              {activeIntegrationTab === 'agents' && (
                <div className="integration-list">
                  {loadingIntegrations ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                      <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem' }} />
                      <p>Carregando agentes...</p>
                    </div>
                  ) : agents.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                      <AppIcon name="smart_toy" style={{ fontSize: '2rem' }} />
                      <p>Nenhum agente configurado</p>
                    </div>
                  ) : (
                    agents.map(agent => (
                    <div
                      key={agent.id}
                      className={`integration-item ${
                        projectData.agents.includes(agent.id) ? 'selected' : ''
                      } ${agent.status === 'inactive' ? 'disabled' : ''}`}
                      onClick={() => agent.status === 'active' && handleToggleAgent(agent.id)}
                    >
                      <AppIcon name={agent.icon} style={{ color: agent.color }} />
                      <div className="integration-info">
                        <span className="integration-name">{agent.name}</span>
                        <span className="integration-status">
                          {agent.status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      {projectData.agents.includes(agent.id) && (
                        <AppIcon name="check_circle" className="check-icon" />
                      )}
                    </div>
                  )))}
                </div>
              )}

              {/* Resumo */}
              <div className="integration-summary">
                <AppIcon name="info" />
                <span>
                  {projectData.connections.length} conexão(ões) • {projectData.agents.length} agente(s) selecionado(s)
                </span>
              </div>
            </div>
            </Step>
          )}

          {/* STEP 5: Configuração CRM (apenas para CRM) */}
          {projectType === 'crm' && (
            <Step>
              <div className="step-header">
                <AppIcon name="assignment" className="step-icon" />
                <h2>Formulário de Qualificação</h2>
                <p>Configure o formulário que será usado para qualificar leads</p>
              </div>

              <div className="step-content">
                {/* Toggle Formulário */}
                <div className="form-toggle-section">
                  <div 
                    className={`toggle-option ${projectData.useForm ? 'active' : ''}`}
                    onClick={() => setProjectData({...projectData, useForm: !projectData.useForm})}
                  >
                    <div className="toggle-header">
                      <AppIcon name={projectData.useForm ? 'check_box' : 'check_box_outline_blank'} />
                      <div className="toggle-content">
                        <h4>Usar Formulário de Qualificação</h4>
                        <p>Ative para coletar informações estruturadas dos leads</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Campos do Formulário */}
                {projectData.useForm && (
                  <div className="form-fields-section">
                    <div className="fields-header">
                      <h4 className="section-subtitle">
                        <AppIcon name="list" />
                        Campos do Formulário
                      </h4>
                      <button
                        type="button"
                        className="btn-add-field"
                        onClick={() => setShowAddFieldModal(true)}
                      >
                        <AppIcon name="add" />
                        Adicionar Campo
                      </button>
                    </div>
                    <p className="section-hint">
                      Personalize os campos que serão coletados no formulário de qualificação
                    </p>

                    <div className="default-fields-list">
                      {projectData.formFields.map(field => (
                        <div key={field.id} className="field-item">
                          <AppIcon name={field.icon} />
                          
                          {editingField === field.id ? (
                            // Modo de edição
                            <div className="field-edit-mode">
                              <input
                                type="text"
                                value={editFieldName}
                                onChange={(e) => setEditFieldName(e.target.value)}
                                className="field-edit-input"
                                placeholder="Nome do campo"
                              />
                              <label className="field-required-toggle">
                                <input
                                  type="checkbox"
                                  checked={editFieldRequired}
                                  onChange={(e) => setEditFieldRequired(e.target.checked)}
                                />
                                <span>Obrigatório</span>
                              </label>
                              <div className="field-edit-actions">
                                <button
                                  type="button"
                                  className="btn-save-field"
                                  onClick={handleSaveField}
                                >
                                  <AppIcon name="check" />
                                </button>
                                <button
                                  type="button"
                                  className="btn-cancel-field"
                                  onClick={handleCancelEdit}
                                >
                                  <AppIcon name="close" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Modo de visualização
                            <>
                              <div className="field-info">
                                <span className="field-name">{field.name}</span>
                                <span className={`field-badge ${field.required ? 'required' : 'optional'}`}>
                                  {field.required ? 'Obrigatório' : 'Opcional'}
                                </span>
                              </div>
                              <div className="field-actions">
                                <button
                                  type="button"
                                  className="btn-icon-field"
                                  onClick={() => handleEditField(field)}
                                  title="Editar campo"
                                >
                                  <AppIcon name="edit" />
                                </button>
                                <button
                                  type="button"
                                  className="btn-icon-field delete"
                                  onClick={() => handleDeleteField(field.id)}
                                  title="Remover campo"
                                >
                                  <AppIcon name="delete" />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="form-info-box">
                      <AppIcon name="lightbulb" />
                      <div>
                        <strong>Como funciona:</strong>
                        <ul>
                          <li>Leads novos chegam na lista <strong>"Leads Recebidos"</strong></li>
                          <li>Durante o atendimento, você preenche o formulário</li>
                          <li>Quando todos os campos obrigatórios forem preenchidos, o lead é automaticamente movido para <strong>"Equipe de Vendas"</strong></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Step>
          )}

          {projectType === 'bar_restaurant' && (
            <Step>
              <div className="step-header">
                <AppIcon name="restaurant_menu" className="step-icon" />
                <h2>Tipo de Projeto (Bar &amp; Restaurante)</h2>
                <p>Selecione o tipo dentro do modelo Bar &amp; Restaurante</p>
              </div>

              <div className="step-content">
                <div className="project-types-grid-step">
                  <div
                    className={`project-type-card-step ${projectData.barRestaurantProjectType === 'service_management' ? 'selected' : ''}`}
                    onClick={() => setProjectData({ ...projectData, barRestaurantProjectType: 'service_management' })}
                  >
                    <AppIcon name="support_agent" className="type-icon" />
                    <span className="type-label">Gestão de atendimento (salão/cozinha/bar)</span>
                    <span className="type-description">Pedidos via WhatsApp, confirmação e Kanban de Mesas e Pedidos</span>
                  </div>
                </div>
              </div>
            </Step>
          )}

          {projectType === 'gas_distributor' && (
            <Step>
              <div className="step-header">
                <AppIcon name="shopping_cart" className="step-icon" />
                <h2>Produtos Disponíveis</h2>
                <p>Selecione quais produtos estão disponíveis neste projeto</p>
              </div>

              <div className="step-content">
                {/* Seleção de Categoria */}
                <div className="form-field-step">
                  <label>
                    <AppIcon name="category" />
                    Categorias de Produtos
                  </label>
                  <p className="field-hint">Selecione uma categoria para ver os produtos</p>

                  {loadingCategories ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                      <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem' }} />
                      <p>Carregando categorias...</p>
                    </div>
                  ) : productCategories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                      <AppIcon name="folder_off" style={{ fontSize: '2rem' }} />
                      <p>Nenhuma categoria de produtos encontrada</p>
                    </div>
                  ) : (
                    <div className="integration-list">
                      {productCategories.map(category => (
                        <div
                          key={category.id}
                          className={`integration-item ${selectedCategory?.id === category.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedCategory(category);
                            fetchProductsByCategory(category.id);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <AppIcon name="category" style={{ color: '#f59e0b' }} />
                          <div className="integration-info">
                            <span className="integration-name">{category.nome}</span>
                            <span className="integration-status">
                              {category.descricao || 'Sem descrição'}
                            </span>
                          </div>
                          {selectedCategory?.id === category.id && (
                            <AppIcon name="check_circle" className="check-icon" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Produtos da Categoria Selecionada */}
                {selectedCategory && (
                  <div className="form-field-step">
                    <label>
                      <AppIcon name="inventory_2" />
                      Produtos - {selectedCategory.nome}
                    </label>
                    <p className="field-hint">Selecione os produtos disponíveis para este projeto</p>

                    {loadingProducts ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                        <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem' }} />
                        <p>Carregando produtos...</p>
                      </div>
                    ) : categoryProducts.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                        <AppIcon name="shopping_bag" style={{ fontSize: '2rem' }} />
                        <p>Nenhum produto nesta categoria</p>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                        {categoryProducts.map(product => (
                          <div
                            key={product.id}
                            style={{
                              padding: '12px',
                              border: `2px solid ${projectData.produtosDisponiveis?.some(p => p.id === product.id) ? '#10b981' : '#e5e7eb'}`,
                              borderRadius: '8px',
                              cursor: 'pointer',
                              backgroundColor: projectData.produtosDisponiveis?.some(p => p.id === product.id) ? 'rgba(16, 185, 129, 0.1)' : '#f9fafb',
                              transition: 'all 0.3s'
                            }}
                            onClick={() => {
                              const current = projectData.produtosDisponiveis || [];
                              const existingIndex = current.findIndex(p => p.id === product.id);
                              
                              if (existingIndex >= 0) {
                                // Remover produto
                                setProjectData({
                                  ...projectData,
                                  produtosDisponiveis: current.filter(p => p.id !== product.id)
                                });
                              } else {
                                // Adicionar produto completo
                                const productData = {
                                  id: product.id,
                                  nome: product.nome,
                                  descricao: product.descricao,
                                  preco_venda: product.preco_venda,
                                  categoria_id: product.categoria_id
                                };
                                setProjectData({
                                  ...projectData,
                                  produtosDisponiveis: [...current, productData]
                                });
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                                  {product.nome}
                                </p>
                                <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#6b7280' }}>
                                  {product.descricao || 'Sem descrição'}
                                </p>
                                <p style={{ margin: '0', fontSize: '13px', fontWeight: '600', color: '#10b981' }}>
                                  R$ {parseFloat(product.preco_venda || 0).toFixed(2)}
                                </p>
                              </div>
                              {projectData.produtosDisponiveis?.some(p => p.id === product.id) && (
                                <AppIcon name="check_circle" style={{ color: '#10b981', fontSize: '20px' }} />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Step>
          )}

          {projectType === 'gas_distributor' && (
            <Step>
              <div className="step-header">
                <AppIcon name="business" className="step-icon" />
                <h2>Configuração da Empresa</h2>
                <p>Informações da empresa (pré-preenchidas de "Minha Empresa")</p>
              </div>

              <div className="step-content">
                {loadingCompanyData ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
                    <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem' }} />
                    <p>Carregando informações da empresa...</p>
                  </div>
                ) : (
                  <>
                    {/* Nome Fantasia */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="business" />
                        Nome Fantasia
                      </label>
                      <input
                        type="text"
                        value={companyDataEtapa6.nomeFantasia}
                        onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, nomeFantasia: e.target.value})}
                        className="input-step"
                        placeholder="Nome da empresa"
                      />
                    </div>

                    {/* Descrição da Empresa */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="description" />
                        Descrição da Empresa
                      </label>
                      <textarea
                        value={companyDataEtapa6.descricao}
                        onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, descricao: e.target.value})}
                        rows={3}
                        className="textarea-step"
                        placeholder="Descreva sua empresa..."
                      />
                    </div>

                    {/* SEÇÃO 2: Endereço */}
                    <div className="form-field-step" style={{ backgroundColor: 'rgba(68, 225, 255, 0.05)', border: '1px solid rgba(68, 225, 255, 0.2)', borderRadius: '8px', padding: '12px', marginBottom: '20px', marginTop: '20px' }}>
                      <label style={{ color: '#44e1ff', marginBottom: '12px' }}>
                        <AppIcon name="location_on" />
                        Endereço da Empresa
                      </label>
                    </div>

                    {/* CEP */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="mail" />
                        CEP
                      </label>
                      <input
                        type="text"
                        value={companyDataEtapa6.cep}
                        onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, cep: e.target.value})}
                        className="input-step"
                        placeholder="00000-000"
                      />
                    </div>

                    {/* Rua e Número */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                      <div className="form-field-step">
                        <label>Logradouro</label>
                        <input
                          type="text"
                          value={companyDataEtapa6.logradouro}
                          onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, logradouro: e.target.value})}
                          className="input-step"
                          placeholder="Rua, Avenida..."
                        />
                      </div>
                      <div className="form-field-step">
                        <label>Número</label>
                        <input
                          type="text"
                          value={companyDataEtapa6.numero}
                          onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, numero: e.target.value})}
                          className="input-step"
                          placeholder="Nº"
                        />
                      </div>
                    </div>

                    {/* Complemento */}
                    <div className="form-field-step">
                      <label>Complemento</label>
                      <input
                        type="text"
                        value={companyDataEtapa6.complemento}
                        onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, complemento: e.target.value})}
                        className="input-step"
                        placeholder="Sala, Andar..."
                      />
                    </div>

                    {/* Bairro e Cidade */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
                      <div className="form-field-step">
                        <label>Bairro</label>
                        <input
                          type="text"
                          value={companyDataEtapa6.bairro}
                          onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, bairro: e.target.value})}
                          className="input-step"
                          placeholder="Bairro"
                        />
                      </div>
                      <div className="form-field-step">
                        <label>Cidade</label>
                        <input
                          type="text"
                          value={companyDataEtapa6.cidade}
                          onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, cidade: e.target.value})}
                          className="input-step"
                          placeholder="Cidade"
                        />
                      </div>
                    </div>

                    {/* Estado */}
                    <div style={{ maxWidth: '150px' }}>
                      <div className="form-field-step">
                        <label>UF</label>
                        <input
                          type="text"
                          value={companyDataEtapa6.estado}
                          onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, estado: e.target.value.toUpperCase()})}
                          className="input-step"
                          placeholder="UF"
                          maxLength="2"
                        />
                      </div>
                    </div>

                    {/* Email e Website */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-field-step">
                        <label>Email de Contato</label>
                        <input
                          type="email"
                          value={companyDataEtapa6.emailContato}
                          onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, emailContato: e.target.value})}
                          className="input-step"
                          placeholder="contato@empresa.com.br"
                        />
                      </div>
                      <div className="form-field-step">
                        <label>Website</label>
                        <input
                          type="text"
                          value={companyDataEtapa6.site}
                          onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, site: e.target.value})}
                          className="input-step"
                          placeholder="www.empresa.com.br"
                        />
                      </div>
                    </div>

                    {/* Instagram e Facebook */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '20px' }}>
                      <div className="form-field-step">
                        <label>Instagram</label>
                        <input
                          type="text"
                          value={companyDataEtapa6.redesSociais?.instagram || ''}
                          onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, redesSociais: {...companyDataEtapa6.redesSociais, instagram: e.target.value}})}
                          className="input-step"
                          placeholder="@suaempresa"
                        />
                      </div>
                      <div className="form-field-step">
                        <label>Facebook</label>
                        <input
                          type="text"
                          value={companyDataEtapa6.redesSociais?.facebook || ''}
                          onChange={(e) => setCompanyDataEtapa6({...companyDataEtapa6, redesSociais: {...companyDataEtapa6.redesSociais, facebook: e.target.value}})}
                          className="input-step"
                          placeholder="facebook.com/suaempresa"
                        />
                      </div>
                    </div>

                    {/* SEÇÃO 5: Configurações da Distribuidora */}
                    <div className="form-field-step" style={{ backgroundColor: 'rgba(68, 225, 255, 0.05)', border: '1px solid rgba(68, 225, 255, 0.2)', borderRadius: '8px', padding: '12px', marginBottom: '20px', marginTop: '20px' }}>
                      <label style={{ color: '#44e1ff', marginBottom: '12px' }}>
                        <AppIcon name="local_shipping" />
                        Configurações da Distribuidora
                      </label>
                    </div>

                    {/* Horários de Funcionamento */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="schedule" />
                        Horários de Funcionamento
                      </label>
                      <p className="field-hint">Configure os horários de atendimento por dia da semana</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {Object.entries(horariosFuncionamento).map(([dia, info]) => (
                          <div key={dia} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '10px', alignItems: 'center' }}>
                            <input
                              type="text"
                              placeholder="Dia"
                              value={dia.charAt(0).toUpperCase() + dia.slice(1)}
                              disabled
                              className="input-step"
                              style={{ backgroundColor: 'rgba(68, 225, 255, 0.1)', color: '#ffffff', textAlign: 'center', fontWeight: 'bold' }}
                            />
                            <input
                              type="time"
                              value={info.abertura}
                              onChange={(e) => setHorariosFuncionamento({
                                ...horariosFuncionamento,
                                [dia]: { ...info, abertura: e.target.value }
                              })}
                              className="input-step"
                            />
                            <input
                              type="time"
                              value={info.fechamento}
                              onChange={(e) => setHorariosFuncionamento({
                                ...horariosFuncionamento,
                                [dia]: { ...info, fechamento: e.target.value }
                              })}
                              className="input-step"
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={info.ativo}
                                onChange={(e) => setHorariosFuncionamento({
                                  ...horariosFuncionamento,
                                  [dia]: { ...info, ativo: e.target.checked }
                                })}
                              />
                              <span style={{ fontSize: '12px' }}>Ativo</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tempo Médio de Entrega */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="timer" />
                        Tempo Médio de Entrega (minutos)
                      </label>
                      <input
                        type="number"
                        placeholder="Ex: 60"
                        value={tempoMedioEntrega}
                        onChange={(e) => setTempoMedioEntrega(e.target.value)}
                        className="input-step"
                      />
                    </div>

                    {/* Formas de Pagamento - Estruturado */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="payment" />
                        Formas de Pagamento Aceitas
                      </label>
                      <p className="field-hint">Configure cada forma de pagamento com suas opções específicas</p>

                      {/* Dinheiro */}
                      <div style={{ backgroundColor: 'rgba(68, 225, 255, 0.05)', border: '1px solid rgba(68, 225, 255, 0.2)', borderRadius: '8px', padding: '12px', marginTop: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
                          <input
                            type="checkbox"
                            checked={formasPagamentoEstruturado[0]?.ativo || false}
                            onChange={(e) => {
                              const updated = [...formasPagamentoEstruturado];
                              updated[0].ativo = e.target.checked;
                              setFormasPagamentoEstruturado(updated);
                            }}
                          />
                          <span style={{ fontWeight: '600' }}>💵 Dinheiro</span>
                        </label>
                        {formasPagamentoEstruturado[0]?.ativo && (
                          <>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '8px' }}>
                              <input
                                type="checkbox"
                                checked={formasPagamentoEstruturado[0]?.naEntrega || false}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[0].naEntrega = e.target.checked;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                              />
                              <span style={{ fontSize: '14px' }}>Pagamento na entrega</span>
                            </label>
                            <div className="form-field-step" style={{ marginTop: '8px' }}>
                              <label style={{ fontSize: '13px' }}>Observações</label>
                              <textarea
                                value={formasPagamentoEstruturado[0]?.observacoes || ''}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[0].observacoes = e.target.value;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                                rows={2}
                                className="textarea-step"
                                placeholder="Ex: Troco disponível até R$ 100"
                                style={{ fontSize: '13px' }}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* PIX */}
                      <div style={{ backgroundColor: 'rgba(68, 225, 255, 0.05)', border: '1px solid rgba(68, 225, 255, 0.2)', borderRadius: '8px', padding: '12px', marginTop: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
                          <input
                            type="checkbox"
                            checked={formasPagamentoEstruturado[1]?.ativo || false}
                            onChange={(e) => {
                              const updated = [...formasPagamentoEstruturado];
                              updated[1].ativo = e.target.checked;
                              setFormasPagamentoEstruturado(updated);
                            }}
                          />
                          <span style={{ fontWeight: '600' }}>📱 PIX</span>
                        </label>
                        {formasPagamentoEstruturado[1]?.ativo && (
                          <>
                            <div className="form-field-step" style={{ marginBottom: '12px' }}>
                              <label style={{ fontSize: '13px' }}>Tipo de PIX</label>
                              <select
                                value={formasPagamentoEstruturado[1]?.tipoPix || 'chave'}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[1].tipoPix = e.target.value;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                                className="input-step"
                                style={{ fontSize: '13px' }}
                              >
                                <option value="chave">Chave PIX</option>
                                <option value="telefone">Telefone</option>
                                <option value="email">Email</option>
                                <option value="cpf">CPF</option>
                              </select>
                            </div>
                            <div className="form-field-step" style={{ marginBottom: '12px' }}>
                              <label style={{ fontSize: '13px' }}>Chave/Valor PIX</label>
                              <input
                                type="text"
                                value={formasPagamentoEstruturado[1]?.chavePix || ''}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[1].chavePix = e.target.value;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                                className="input-step"
                                placeholder={formasPagamentoEstruturado[1]?.tipoPix === 'chave' ? 'xxx@empresa.com' : 'Valor da chave'}
                                style={{ fontSize: '13px' }}
                              />
                            </div>
                            <div className="form-field-step">
                              <label style={{ fontSize: '13px' }}>Observações</label>
                              <textarea
                                value={formasPagamentoEstruturado[1]?.observacoes || ''}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[1].observacoes = e.target.value;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                                rows={2}
                                className="textarea-step"
                                placeholder="Ex: PIX instantâneo, confirmação imediata"
                                style={{ fontSize: '13px' }}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* Cartão */}
                      <div style={{ backgroundColor: 'rgba(68, 225, 255, 0.05)', border: '1px solid rgba(68, 225, 255, 0.2)', borderRadius: '8px', padding: '12px', marginTop: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
                          <input
                            type="checkbox"
                            checked={formasPagamentoEstruturado[2]?.ativo || false}
                            onChange={(e) => {
                              const updated = [...formasPagamentoEstruturado];
                              updated[2].ativo = e.target.checked;
                              setFormasPagamentoEstruturado(updated);
                            }}
                          />
                          <span style={{ fontWeight: '600' }}>💳 Cartão</span>
                        </label>
                        {formasPagamentoEstruturado[2]?.ativo && (
                          <>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '8px' }}>
                              <input
                                type="checkbox"
                                checked={formasPagamentoEstruturado[2]?.naEntrega || false}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[2].naEntrega = e.target.checked;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                              />
                              <span style={{ fontSize: '14px' }}>Pagamento na máquina (na entrega)</span>
                            </label>
                            <div className="form-field-step" style={{ marginTop: '8px', marginBottom: '12px' }}>
                              <label style={{ fontSize: '13px' }}>Máquina/Operadora</label>
                              <input
                                type="text"
                                value={formasPagamentoEstruturado[2]?.maquinaPagamento || ''}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[2].maquinaPagamento = e.target.value;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                                className="input-step"
                                placeholder="Ex: Cielo, Stone, Rede"
                                style={{ fontSize: '13px' }}
                              />
                            </div>
                            <div className="form-field-step">
                              <label style={{ fontSize: '13px' }}>Observações</label>
                              <textarea
                                value={formasPagamentoEstruturado[2]?.observacoes || ''}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[2].observacoes = e.target.value;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                                rows={2}
                                className="textarea-step"
                                placeholder="Ex: Débito e crédito, sem juros"
                                style={{ fontSize: '13px' }}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* Outros */}
                      <div style={{ backgroundColor: 'rgba(68, 225, 255, 0.05)', border: '1px solid rgba(68, 225, 255, 0.2)', borderRadius: '8px', padding: '12px', marginTop: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
                          <input
                            type="checkbox"
                            checked={formasPagamentoEstruturado.length > 3 && formasPagamentoEstruturado[3]?.ativo}
                            onChange={(e) => {
                              if (e.target.checked && formasPagamentoEstruturado.length === 3) {
                                setFormasPagamentoEstruturado([...formasPagamentoEstruturado, {
                                  tipo: 'outros',
                                  ativo: true,
                                  nome: '',
                                  observacoes: ''
                                }]);
                              } else if (!e.target.checked && formasPagamentoEstruturado.length > 3) {
                                setFormasPagamentoEstruturado(formasPagamentoEstruturado.slice(0, 3));
                              }
                            }}
                          />
                          <span style={{ fontWeight: '600' }}>🔧 Outros</span>
                        </label>
                        {formasPagamentoEstruturado.length > 3 && formasPagamentoEstruturado[3]?.ativo && (
                          <>
                            <div className="form-field-step" style={{ marginBottom: '12px' }}>
                              <label style={{ fontSize: '13px' }}>Nome da Forma de Pagamento</label>
                              <input
                                type="text"
                                value={formasPagamentoEstruturado[3]?.nome || ''}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[3].nome = e.target.value;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                                className="input-step"
                                placeholder="Ex: Transferência bancária, Boleto"
                                style={{ fontSize: '13px' }}
                              />
                            </div>
                            <div className="form-field-step">
                              <label style={{ fontSize: '13px' }}>Informações/Observações</label>
                              <textarea
                                value={formasPagamentoEstruturado[3]?.observacoes || ''}
                                onChange={(e) => {
                                  const updated = [...formasPagamentoEstruturado];
                                  updated[3].observacoes = e.target.value;
                                  setFormasPagamentoEstruturado(updated);
                                }}
                                rows={3}
                                className="textarea-step"
                                placeholder="Ex: Transferência para conta corrente, boleto com vencimento em 3 dias"
                                style={{ fontSize: '13px' }}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Áreas de Cobertura */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="map" />
                        Áreas de Cobertura
                      </label>
                      <p className="field-hint">Adicione bairros/regiões com taxa de entrega</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {areasCobertura.map((area, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '10px', alignItems: 'center' }}>
                            <input
                              type="text"
                              placeholder="Bairro/Região"
                              value={area.nome}
                              disabled
                              className="input-step"
                              style={{ backgroundColor: '#f3f4f6' }}
                            />
                            <input
                              type="number"
                              placeholder="Taxa (R$)"
                              value={area.taxa}
                              disabled
                              className="input-step"
                              style={{ backgroundColor: '#f3f4f6' }}
                            />
                            <input
                              type="number"
                              placeholder="Tempo (min)"
                              value={area.tempo}
                              disabled
                              className="input-step"
                              style={{ backgroundColor: '#f3f4f6' }}
                            />
                            <button
                              type="button"
                              className="btn-cancel-modal"
                              onClick={() => setAreasCobertura(areasCobertura.filter((_, i) => i !== idx))}
                            >
                              Remover
                            </button>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '10px', marginTop: '12px' }}>
                        <input
                          type="text"
                          placeholder="Bairro/Região"
                          value={novaArea.nome}
                          onChange={(e) => setNovaArea({...novaArea, nome: e.target.value})}
                          className="input-step"
                        />
                        <input
                          type="number"
                          placeholder="Taxa (R$)"
                          value={novaArea.taxa}
                          onChange={(e) => setNovaArea({...novaArea, taxa: e.target.value})}
                          className="input-step"
                        />
                        <input
                          type="number"
                          placeholder="Tempo (min)"
                          value={novaArea.tempo}
                          onChange={(e) => setNovaArea({...novaArea, tempo: e.target.value})}
                          className="input-step"
                        />
                        <button
                          type="button"
                          className="btn-add-field"
                          onClick={() => {
                            if (novaArea.nome && novaArea.taxa && novaArea.tempo) {
                              setAreasCobertura([...areasCobertura, novaArea]);
                              setNovaArea({ nome: '', taxa: '', tempo: '' });
                            }
                          }}
                        >
                          <AppIcon name="add" />
                        </button>
                      </div>
                    </div>

                    {/* Política de Entrega */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="local_shipping" />
                        Política de Entrega
                      </label>
                      <textarea
                        placeholder="Ex: Entrega em até 60 minutos na região de cobertura..."
                        value={politicaEntrega}
                        onChange={(e) => setPoliticaEntrega(e.target.value)}
                        rows={4}
                        className="textarea-step"
                      />
                    </div>

                    {/* Política de Cancelamento */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="cancel" />
                        Política de Cancelamento
                      </label>
                      <textarea
                        placeholder="Ex: Cancelamento permitido até 30 minutos após confirmação..."
                        value={politicaCancelamento}
                        onChange={(e) => setPoliticaCancelamento(e.target.value)}
                        rows={4}
                        className="textarea-step"
                      />
                    </div>

                    {/* Política de Vendas e Valores */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="payments" />
                        Política de Vendas e Valores
                      </label>
                      <textarea
                        placeholder="Ex: Pedido mínimo de R$ 50,00. Desconto de 5% para pagamento PIX..."
                        value={politicaVendas}
                        onChange={(e) => setPoliticaVendas(e.target.value)}
                        rows={4}
                        className="textarea-step"
                      />
                    </div>
                  </>
                )}
              </div>
            </Step>
          )}

          {/* STEP 7: Revisão - Gas Distributor */}
          {projectType === 'gas_distributor' && (
            <Step>
              <div className="step-header">
                <AppIcon name="check_circle" className="step-icon" />
                <h2>Revisão do Projeto</h2>
                <p>Verifique todas as informações antes de criar</p>
              </div>

              <div className="step-content">
                <div className="form-field-step">
                  <label>
                    <AppIcon name="info" />
                    Informações Básicas
                  </label>
                  <div style={{ padding: '12px', backgroundColor: 'rgba(68, 225, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(68, 225, 255, 0.2)' }}>
                    <div style={{ marginBottom: '8px' }}><strong>Nome:</strong> {projectData.name}</div>
                    <div style={{ marginBottom: '8px' }}><strong>Descrição:</strong> {projectData.description}</div>
                    <div><strong>Membros:</strong> {projectData.team?.length || 0} selecionados</div>
                  </div>
                </div>

                <div className="form-field-step">
                  <label>
                    <AppIcon name="link" />
                    Conexão WhatsApp
                  </label>
                  <div style={{ padding: '12px', backgroundColor: 'rgba(68, 225, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(68, 225, 255, 0.2)' }}>
                    <div style={{ marginBottom: '8px' }}><strong>Conexão:</strong> {connections.find(c => c.id === projectData.connections?.[0])?.name || 'Não selecionada'}</div>
                    <div><strong>Grupo:</strong> {projectData.grupoWhatsappNome || 'Não selecionado'}</div>
                  </div>
                </div>

                <div className="form-field-step">
                  <label>
                    <AppIcon name="shopping_cart" />
                    Produtos Selecionados
                  </label>
                  <div style={{ padding: '12px', backgroundColor: 'rgba(68, 225, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(68, 225, 255, 0.2)' }}>
                    <div><strong>Total:</strong> {projectData.produtosDisponiveis?.length || 0} produtos</div>
                  </div>
                </div>

                <div className="form-field-step">
                  <label>
                    <AppIcon name="business" />
                    Configuração da Empresa
                  </label>
                  <div style={{ padding: '12px', backgroundColor: 'rgba(68, 225, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(68, 225, 255, 0.2)' }}>
                    <div style={{ marginBottom: '8px' }}><strong>Endereço Base:</strong> {enderecoBaseEstruturado.logradouro && `${enderecoBaseEstruturado.logradouro}, ${enderecoBaseEstruturado.numero} - ${enderecoBaseEstruturado.bairro}, ${enderecoBaseEstruturado.cidade} - ${enderecoBaseEstruturado.estado}` || 'Não informado'}</div>
                    <div style={{ marginBottom: '8px' }}><strong>Tempo Médio de Entrega:</strong> {tempoMedioEntrega} minutos</div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Formas de Pagamento:</strong>
                      <div style={{ marginTop: '4px', paddingLeft: '16px' }}>
                        {formasPagamentoEstruturado.filter(f => f.ativo).map((forma, idx) => (
                          <div key={idx}>
                            {forma.tipo === 'dinheiro' && 'Dinheiro'}
                            {forma.tipo === 'pix' && 'PIX'}
                            {forma.tipo === 'cartao' && 'Cartão'}
                            {forma.tipo === 'outros' && forma.nome}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <strong>Áreas de Cobertura:</strong>
                      <div style={{ marginTop: '4px', paddingLeft: '16px' }}>
                        {areasCobertura.length > 0 ? (
                          areasCobertura.map((area, idx) => (
                            <div key={idx}>{area.nome} - R$ {area.taxa} ({area.tempo} min)</div>
                          ))
                        ) : (
                          <div style={{ color: '#9bb3c7' }}>Nenhuma área configurada</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-field-step">
                  <label>
                    <AppIcon name="schedule" />
                    Horários de Funcionamento
                  </label>
                  <div style={{ padding: '12px', backgroundColor: 'rgba(68, 225, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(68, 225, 255, 0.2)' }}>
                    {Object.entries(horariosFuncionamento).map(([dia, info]) => (
                      <div key={dia} style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                        <strong>{dia.charAt(0).toUpperCase() + dia.slice(1)}:</strong>
                        <span>{info.ativo ? `${info.abertura} - ${info.fechamento}` : 'Fechado'}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-field-step">
                  <label>
                    <AppIcon name="description" />
                    Políticas
                  </label>
                  <div style={{ padding: '12px', backgroundColor: 'rgba(68, 225, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(68, 225, 255, 0.2)' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Política de Entrega:</strong>
                      <p style={{ margin: '4px 0 0 0', color: '#9bb3c7' }}>{politicaEntrega || 'Não configurada'}</p>
                    </div>
                    <div>
                      <strong>Política de Cancelamento:</strong>
                      <p style={{ margin: '4px 0 0 0', color: '#9bb3c7' }}>{politicaCancelamento || 'Não configurada'}</p>
                    </div>
                  </div>
                </div>

                <div className="form-field-step" style={{ backgroundColor: 'rgba(68, 225, 255, 0.05)', border: '1px solid rgba(68, 225, 255, 0.2)', borderRadius: '8px', padding: '12px' }}>
                  <label style={{ marginBottom: '8px' }}>
                    <AppIcon name="smart_toy" />
                    Agente IA
                  </label>
                  <p style={{ margin: '0', color: '#e0e7ff', fontSize: '14px' }}>
                    Um agente de atendimento automático será criado com todas as informações acima. Ele irá atender clientes, coletar pedidos e gerenciar o fluxo de entrega.
                  </p>
                </div>
              </div>
            </Step>
          )}

          {projectType === 'bar_restaurant' && (
            <Step>
              <div className="step-header">
                <AppIcon name="group_add" className="step-icon" />
                <h2>Integrantes do Projeto</h2>
                <p>Adicione os integrantes e o número de WhatsApp de cada um</p>
              </div>

              <div className="step-content">
                <div className="form-field-step">
                  <label>
                    <AppIcon name="phone" />
                    Números do time (WhatsApp)
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {(projectData.barRestaurantTeam || []).map((m) => (
                      <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder="Nome (ex: João - Garçom)"
                          value={m.name}
                          onChange={(e) => handleUpdateBarRestaurantTeamMember(m.id, { name: e.target.value })}
                          className="input-step"
                        />
                        <input
                          type="text"
                          placeholder="WhatsApp (ex: 5511999999999)"
                          value={m.phone}
                          onChange={(e) => handleUpdateBarRestaurantTeamMember(m.id, { phone: e.target.value })}
                          className="input-step"
                        />
                        <button
                          type="button"
                          className="btn-cancel-modal"
                          onClick={() => handleRemoveBarRestaurantTeamMember(m.id)}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                    <button type="button" className="btn-add-field" onClick={handleAddBarRestaurantTeamMember}>
                      <AppIcon name="add" />
                      Adicionar integrante
                    </button>
                  </div>
                  <span className="field-hint">Visual apenas por enquanto. Depois vamos usar esses números para criar o grupo automaticamente.</span>
                </div>
              </div>
            </Step>
          )}

          {projectType === 'bar_restaurant' && (
            <Step>
              <div className="step-header">
                <AppIcon name="upload_file" className="step-icon" />
                <h2>Informações da Casa</h2>
                <p>Envie documentos (cardápio, preços, regras) e/ou digite informações extras</p>
              </div>

              <div className="step-content">
                <div className="form-field-step">
                  <label>
                    <AppIcon name="attach_file" />
                    Enviar Cardápio/Documento
                  </label>
                  <input
                    type="file"
                    onChange={handleBarRestaurantFilesChange}
                    className="input-step"
                  />
                  <span className="field-hint">
                    {projectData.barRestaurantFiles?.length ? `✅ ${projectData.barRestaurantFiles[0].name} (${projectData.barRestaurantExtractedText.length} caracteres)` : 'Selecione um arquivo (TXT, CSV, PDF ou imagem)'}
                  </span>
                </div>

                {projectData.barRestaurantExtractedText && (
                  <div className="form-field-step">
                    <label>
                      <AppIcon name="description" />
                      Texto Extraído do Documento
                    </label>
                    <textarea
                      placeholder="Texto extraído do arquivo..."
                      value={projectData.barRestaurantExtractedText}
                      onChange={(e) => setProjectData({ ...projectData, barRestaurantExtractedText: e.target.value })}
                      rows={8}
                      className="textarea-step"
                    />
                    <span className="field-hint">✏️ Você pode editar o texto extraído se necessário</span>
                  </div>
                )}

                <div className="form-field-step">
                  <label>
                    <AppIcon name="edit_note" />
                    Informações Adicionais (texto livre)
                  </label>
                  <textarea
                    placeholder="Ex: horários, regras do salão, ingredientes, políticas, observações importantes..."
                    value={projectData.barRestaurantHouseInfo}
                    onChange={(e) => setProjectData({ ...projectData, barRestaurantHouseInfo: e.target.value })}
                    rows={6}
                    className="textarea-step"
                  />
                  <span className="field-hint">Informações adicionais que serão incluídas no prompt do agente</span>
                </div>
              </div>
            </Step>
          )}

          {projectType === 'bar_restaurant' && (
            <Step>
              <div className="step-header">
                <AppIcon name="smart_toy" className="step-icon" />
                <h2>Atendente do Salão - Revisão</h2>
                <p>Revise e ajuste o prompt do agente antes de criar o projeto</p>
              </div>

              <div className="step-content">
                <div className="form-field-step">
                  <label>
                    <AppIcon name="badge" />
                    Nome do Agente
                  </label>
                  <input
                    type="text"
                    value={projectData.barRestaurantAgentDraft?.name || ''}
                    onChange={(e) => setProjectData({
                      ...projectData,
                      barRestaurantAgentDraft: {
                        ...(projectData.barRestaurantAgentDraft || {}),
                        name: e.target.value,
                      }
                    })}
                    className="input-step"
                    placeholder="Ex: Atendente do Salão"
                  />
                </div>

                {promptError && (
                  <div style={{
                    padding: '12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    color: '#ef4444',
                    marginBottom: '12px',
                    fontSize: '0.9rem'
                  }}>
                    <AppIcon name="warning" style={{ fontSize: '1.2rem', verticalAlign: 'middle', marginRight: '8px' }} />
                    {promptError}
                  </div>
                )}

                <div className="form-field-step">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label>
                      <AppIcon name="text_snippet" />
                      Prompt do Agente
                    </label>
                    {generatingPrompt && (
                      <span style={{ fontSize: '0.85rem', color: '#44e1ff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <AppIcon name="sync" className="spinning" style={{ fontSize: '1rem' }} />
                        Gerando prompt...
                      </span>
                    )}
                  </div>
                  <textarea
                    placeholder="O prompt será gerado automaticamente com base nas informações do restaurante..."
                    value={projectData.barRestaurantAgentDraft?.prompt || ''}
                    onChange={(e) => setProjectData({
                      ...projectData,
                      barRestaurantAgentDraft: {
                        ...(projectData.barRestaurantAgentDraft || {}),
                        prompt: e.target.value,
                      }
                    })}
                    rows={12}
                    className="textarea-step"
                    disabled={generatingPrompt}
                    style={{ opacity: generatingPrompt ? 0.6 : 1 }}
                  />
                  <span className="field-hint">
                    ✏️ Você pode editar o prompt antes de criar o projeto. Ele será usado para instruir o agente a receber e validar pedidos.
                  </span>
                </div>

                <div style={{
                  padding: '12px',
                  background: 'rgba(68, 225, 255, 0.1)',
                  border: '1px solid rgba(68, 225, 255, 0.3)',
                  borderRadius: '8px',
                  marginTop: '12px'
                }}>
                  <strong style={{ color: '#44e1ff', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <AppIcon name="info" />
                    Como funciona:
                  </strong>
                  <ul style={{ margin: '0', paddingLeft: '24px', fontSize: '0.9rem', color: '#9bb3c7' }}>
                    <li>Garçom envia: "Mesa 4: 1 coca, 1 batata"</li>
                    <li>Agente recebe e estrutura o pedido</li>
                    <li>Agente pede confirmação com botões: ✅ Confirmar / ✏️ Alterar / ❌ Cancelar</li>
                    <li>Conversa aparece normalmente em Atendimentos</li>
                    <li>Pedido confirmado é criado no Kanban</li>
                  </ul>
                </div>

                {/* AGENTE ADMIN */}
                <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid rgba(155, 179, 199, 0.2)' }}>
                  <div className="form-field-step">
                    <label>
                      <AppIcon name="badge" />
                      Nome do Agente Admin
                    </label>
                    <input
                      type="text"
                      value={projectData.barRestaurantAdminAgentDraft?.name || ''}
                      onChange={(e) => setProjectData({
                        ...projectData,
                        barRestaurantAdminAgentDraft: {
                          ...(projectData.barRestaurantAdminAgentDraft || {}),
                          name: e.target.value,
                        }
                      })}
                      className="input-step"
                      placeholder="Ex: Admin - Gerenciador do Grupo"
                    />
                  </div>

                  <div className="form-field-step">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label>
                        <AppIcon name="text_snippet" />
                        Prompt do Agente Admin
                      </label>
                      {generatingPrompt && (
                        <span style={{ fontSize: '0.85rem', color: '#44e1ff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <AppIcon name="sync" className="spinning" style={{ fontSize: '1rem' }} />
                          Gerando prompt...
                        </span>
                      )}
                    </div>
                    <textarea
                      placeholder="O prompt será gerado automaticamente com base nas informações do restaurante..."
                      value={projectData.barRestaurantAdminAgentDraft?.prompt || ''}
                      onChange={(e) => setProjectData({
                        ...projectData,
                        barRestaurantAdminAgentDraft: {
                          ...(projectData.barRestaurantAdminAgentDraft || {}),
                          prompt: e.target.value,
                        }
                      })}
                      rows={12}
                      className="textarea-step"
                      disabled={generatingPrompt}
                      style={{ opacity: generatingPrompt ? 0.6 : 1 }}
                    />
                    <span className="field-hint">
                      ✏️ Você pode editar o prompt antes de criar o projeto. Ele será usado para gerenciar o grupo WhatsApp e coordenar mesas/kanban.
                    </span>
                  </div>

                  <div style={{
                    padding: '12px',
                    background: 'rgba(68, 225, 255, 0.1)',
                    border: '1px solid rgba(68, 225, 255, 0.3)',
                    borderRadius: '8px',
                    marginTop: '12px'
                  }}>
                    <strong style={{ color: '#44e1ff', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <AppIcon name="info" />
                      Como funciona o Admin:
                    </strong>
                    <ul style={{ margin: '0', paddingLeft: '24px', fontSize: '0.9rem', color: '#9bb3c7' }}>
                      <li>Atendente envia pedido estruturado para o grupo</li>
                      <li>Admin recebe e verifica se a mesa existe</li>
                      <li>Se não existe: cria a mesa e adiciona o pedido</li>
                      <li>Se existe: adiciona o pedido à mesa existente</li>
                      <li>Atualiza o kanban do projeto automaticamente</li>
                      <li>Envia confirmação no grupo com botões para alterar status</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Step>
          )}

          {/* STEP 5/6: Personalização */}
          <Step>
            <div className="step-header">
              <AppIcon name="palette" className="step-icon" />
              <h2>Personalização</h2>
              <p>Personalize a aparência do seu projeto</p>
            </div>

            <div className="step-content">
              {/* Upload de Plano de Fundo */}
              <div className="image-upload-section">
                <label className="upload-label">
                  <AppIcon name="image" />
                  Plano de Fundo (Opcional)
                </label>
                <p className="upload-hint">Adicione uma imagem de capa para o seu projeto (máx. 5MB)</p>
                
                {!projectData.backgroundImagePreview ? (
                  <label 
                    className="upload-area"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <AppIcon name="cloud_upload" className="upload-icon" />
                    <span className="upload-text">Clique para fazer upload ou arraste a imagem aqui</span>
                    <span className="upload-formats">PNG, JPG, GIF até 5MB</span>
                  </label>
                ) : (
                  <div className="image-preview-container">
                    <img src={projectData.backgroundImagePreview} alt="Preview" className="image-preview" />
                    <button 
                      type="button"
                      className="remove-image-btn"
                      onClick={handleRemoveImage}
                    >
                      <AppIcon name="close" />
                      Remover imagem
                    </button>
                  </div>
                )}
              </div>

              {/* Seleção de Cor */}
              <div className="color-section">
                <label className="color-label">
                  <AppIcon name="palette" />
                  Cor do Projeto
                </label>
                <div className="color-picker-step">
                {availableColors.map(({ color, name }) => (
                  <div
                    key={color}
                    className={`color-option-step ${projectData.color === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setProjectData({...projectData, color})}
                    title={name}
                  >
                    {projectData.color === color && (
                      <AppIcon name="check" />
                    )}
                  </div>
                ))}
                </div>
              </div>

              <div className="project-preview">
                <span className="preview-label">Prévia do projeto:</span>
                <div className="preview-card" style={{ borderTopColor: projectData.color }}>
                  <div className="preview-icon" style={{ backgroundColor: projectData.color }}>
                    <AppIcon name={projectData.icon} />
                  </div>
                  <div className="preview-info">
                    <h3>{projectData.name || 'Nome do Projeto'}</h3>
                    <p>{projectData.description || 'Descrição do projeto...'}</p>
                  </div>
                </div>
              </div>
            </div>
          </Step>

          {/* STEP 6: Resumo Final */}
          <Step>
            <div className="step-header">
              <AppIcon name="summarize" className="step-icon" />
              <h2>Resumo do Projeto</h2>
              <p>Revise as configurações antes de criar o projeto</p>
            </div>

            <div className="step-content">
              {/* Resumo Geral do Projeto */}
              <div className="project-summary">
                <h4 className="section-subtitle">
                  <AppIcon name="info" />
                  Informações do Projeto
                </h4>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Nome:</span>
                    <span className="summary-value">{projectData.name || 'Não definido'}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Tipo:</span>
                    <span className="summary-value">
                      {projectTypes.find(t => t.value === projectType)?.label || 'Personalizado'}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Membros:</span>
                    <span className="summary-value">
                      {projectData.allUsersSelected ? 'Toda equipe' : `${projectData.members.length} selecionado(s)`}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Conexões:</span>
                    <span className="summary-value">{projectData.connections.length} selecionada(s)</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Agentes IA:</span>
                    <span className="summary-value">{projectData.agents.length} selecionado(s)</span>
                  </div>
                  {projectType === 'crm' && (
                    <div className="summary-item">
                      <span className="summary-label">Formulário:</span>
                      <span className="summary-value">{projectData.useForm ? 'Ativado' : 'Desativado'}</span>
                    </div>
                  )}

                  {projectType === 'bar_restaurant' && (
                    <>
                      <div className="summary-item">
                        <span className="summary-label">Subtipo:</span>
                        <span className="summary-value">
                          {projectData.barRestaurantProjectType === 'service_management'
                            ? 'Gestão de atendimento (salão/cozinha/bar)'
                            : (projectData.barRestaurantProjectType || 'Não definido')}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Integrantes (WhatsApp):</span>
                        <span className="summary-value">{(projectData.barRestaurantTeam || []).filter(m => (m?.name || m?.phone)).length} informado(s)</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Arquivos:</span>
                        <span className="summary-value">{projectData.barRestaurantFiles?.length || 0} selecionado(s)</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Agente (rascunho):</span>
                        <span className="summary-value">{projectData.barRestaurantAgentDraft?.name || 'Não definido'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Preview das Listas CRM */}
              {projectType === 'marketing_campaign' && (
                <div className="crm-lists-preview">
                  <h4 className="section-subtitle">
                    <AppIcon name="view_kanban" />
                    Listas que serão criadas automaticamente
                  </h4>
                  <p className="section-hint">
                    Estrutura no mesmo estilo do CRM, com duas listas antes de &quot;Em atendimento&quot;
                  </p>
                  <div className="lists-preview-grid">
                    <div className="list-preview-card">
                      <AppIcon name="schedule_send" style={{ color: '#f97316' }} />
                      <span className="list-name">Disparo pendente</span>
                      <span className="list-desc">Aguardando disparo</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="send" style={{ color: '#22c55e' }} />
                      <span className="list-name">Disparo efetivado</span>
                      <span className="list-desc">Disparo realizado</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="support_agent" style={{ color: '#f59e0b' }} />
                      <span className="list-name">Em Atendimento</span>
                      <span className="list-desc">Leads sendo atendidos</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="trending_up" style={{ color: '#10b981' }} />
                      <span className="list-name">Equipe de Vendas</span>
                      <span className="list-desc">Prontos para venda</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="check_circle" style={{ color: '#06b6d4' }} />
                      <span className="list-name">Vendas Efetivadas</span>
                      <span className="list-desc">Vendas concluídas</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="schedule" style={{ color: '#ec4899' }} />
                      <span className="list-name">Follow-up 1</span>
                      <span className="list-desc">Primeiro acompanhamento</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="schedule" style={{ color: '#f472b6' }} />
                      <span className="list-name">Follow-up 2</span>
                      <span className="list-desc">Segundo acompanhamento</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="schedule" style={{ color: '#fb7185' }} />
                      <span className="list-name">Follow-up 3</span>
                      <span className="list-desc">Terceiro acompanhamento</span>
                    </div>
                  </div>
                </div>
              )}

              {projectType === 'crm' && (
                <div className="crm-lists-preview">
                  <h4 className="section-subtitle">
                    <AppIcon name="view_kanban" />
                    Listas que serão criadas automaticamente
                  </h4>
                  <p className="section-hint">
                    Estas listas serão criadas no seu projeto CRM para organizar o fluxo de vendas
                  </p>
                  <div className="lists-preview-grid">
                    <div className="list-preview-card">
                      <AppIcon name="inbox" style={{ color: '#3b82f6' }} />
                      <span className="list-name">Leads Recebidos</span>
                      <span className="list-desc">Novos contatos do WhatsApp</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="support_agent" style={{ color: '#f59e0b' }} />
                      <span className="list-name">Em Atendimento</span>
                      <span className="list-desc">Leads sendo atendidos</span>
                    </div>
                    {projectData.useForm && (
                      <div className="list-preview-card">
                        <AppIcon name="assignment" style={{ color: '#8b5cf6' }} />
                        <span className="list-name">Aguardando Formulário</span>
                        <span className="list-desc">Formulário pendente</span>
                      </div>
                    )}
                    <div className="list-preview-card">
                      <AppIcon name="trending_up" style={{ color: '#10b981' }} />
                      <span className="list-name">Equipe de Vendas</span>
                      <span className="list-desc">{projectData.useForm ? 'Leads qualificados' : 'Prontos para venda'}</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="check_circle" style={{ color: '#06b6d4' }} />
                      <span className="list-name">Vendas Efetivadas</span>
                      <span className="list-desc">Vendas concluídas</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="schedule" style={{ color: '#ec4899' }} />
                      <span className="list-name">Follow-up 1</span>
                      <span className="list-desc">Primeiro acompanhamento</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="schedule" style={{ color: '#f472b6' }} />
                      <span className="list-name">Follow-up 2</span>
                      <span className="list-desc">Segundo acompanhamento</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="schedule" style={{ color: '#fb7185' }} />
                      <span className="list-name">Follow-up 3</span>
                      <span className="list-desc">Terceiro acompanhamento</span>
                    </div>
                  </div>
                </div>
              )}

              {projectType === 'bar_restaurant' && (
                <div className="crm-lists-preview">
                  <h4 className="section-subtitle">
                    <AppIcon name="view_kanban" />
                    Listas/Cards que serão criados automaticamente
                  </h4>
                  <p className="section-hint">
                    Prévia da estrutura padrão do projeto Bar &amp; Restaurante (gestão de mesas e pedidos)
                  </p>

                  <div className="lists-preview-grid">
                    <div className="list-preview-card">
                      <AppIcon name="table_restaurant" style={{ color: '#10b981' }} />
                      <span className="list-name">Mesas em aberto</span>
                      <span className="list-desc">Mesas atendendo hoje</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="event_available" style={{ color: '#6b7280' }} />
                      <span className="list-name">Mesas fechadas</span>
                      <span className="list-desc">Mesas finalizadas</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="receipt_long" style={{ color: '#3b82f6' }} />
                      <span className="list-name">Pedidos em aberto</span>
                      <span className="list-desc">Aguardando produção</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="restaurant" style={{ color: '#f59e0b' }} />
                      <span className="list-name">Em produção</span>
                      <span className="list-desc">Cozinha/Bar preparando</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="done_all" style={{ color: '#22c55e' }} />
                      <span className="list-name">Pronto</span>
                      <span className="list-desc">Pronto para entrega</span>
                    </div>
                    <div className="list-preview-card">
                      <AppIcon name="local_shipping" style={{ color: '#06b6d4' }} />
                      <span className="list-name">Entregue</span>
                      <span className="list-desc">Pedido entregue ao cliente</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Step>
        </Stepper>
      </div>

      {/* Modal Adicionar Campo */}
      {showAddFieldModal && (
        <div className="modal-overlay-field" onClick={() => setShowAddFieldModal(false)}>
          <div className="modal-content-field" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-field">
              <h3>Adicionar Novo Campo</h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setShowAddFieldModal(false)}
              >
                <AppIcon name="close" />
              </button>
            </div>

            <div className="modal-body-field">
              <div className="form-field-modal">
                <label>Nome do Campo *</label>
                <input
                  type="text"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  placeholder="Ex: CPF, Endereço, Cargo..."
                  className="input-modal"
                  autoFocus
                />
              </div>

              <div className="form-field-modal">
                <label>Ícone</label>
                <select
                  value={newFieldIcon}
                  onChange={(e) => setNewFieldIcon(e.target.value)}
                  className="select-modal"
                >
                  <option value="label">📝 Padrão</option>
                  <option value="badge">🏷️ Badge</option>
                  <option value="location_on">📍 Localização</option>
                  <option value="work">💼 Trabalho</option>
                  <option value="calendar_today">📅 Data</option>
                  <option value="schedule">📆 Agendamento</option>
                  <option value="attach_money">💰 Dinheiro</option>
                  <option value="description">📄 Descrição</option>
                  <option value="link">🔗 Link</option>
                  <option value="numbers">🔢 Número</option>
                </select>
              </div>

              <div className="form-field-modal">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newFieldRequired}
                    onChange={(e) => setNewFieldRequired(e.target.checked)}
                  />
                  <span>Campo obrigatório</span>
                </label>
              </div>
            </div>

            <div className="modal-footer-field">
              <button
                type="button"
                className="btn-cancel-modal"
                onClick={() => setShowAddFieldModal(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-add-modal"
                onClick={handleAddField}
              >
                <AppIcon name="add" />
                Adicionar Campo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
